import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useAssociation } from '../contexts/AssociationContext';
import ApiServiceParent from '../services/ApiServiceParent.js';
import ApiServiceAdmin from '../services/ApiServiceAdmin';
import { GLOBAL_STYLES, GLOBAL_UTILS } from '../styles/globalStyles';
import { mapQuranRecordFromBackend } from '../utils/quranRecordMapper';
import { QuranRecord } from '../types/api-routes';
import { FiRefreshCw } from 'react-icons/fi';
import QuranRecordSection from './shared/QuranRecordSection';
import QuranTrackingSection from './shared/QuranTrackingSection';
import ChangePasswordModal from './shared/ChangePasswordModal';
import MessageItem, { MessageItemData } from './shared/MessageItem';

interface ParentDashboardProps {
  onBackHome: () => void;
  onAuthError?: () => void;
  currentLanguage?: string;
  translations?: {
    ar: { [key: string]: string };
    fr: { [key: string]: string };
    en: { [key: string]: string };
  };
}

// Wrapper component pour l'icône de rafraîchissement (compatible TypeScript)
const RefreshIcon: React.FC<{ size?: number; style?: React.CSSProperties; animate?: boolean }> = ({ size = 14, style, animate = false }) => {
  const IconComponent = FiRefreshCw as any;
  return <IconComponent size={size} style={style} />;
};


interface LinkedStudent {
  id: number;
  studentId: number; // DTO camelCase
  student_name: string;
  class_name?: string;
  halqa?: string;
  gender?: 'Female' | 'Male';
}

interface Grade {
  id: number;
  studentId: number; // DTO camelCase
  student_name: string;
  subject: string;
  grade: number;
  date: string;
  comment?: string;
}

interface Message {
  id: number;
  subject: string;
  content: string;
  sender_name: string;
  sender_type?: string | null;
  sent_date: string;
  sent_time?: string;
  priority?: 'normal' | 'high' | 'urgent';
  is_read?: boolean;
  student_id?: number | null;
  student_name?: string | null;
  student_gender?: 'Male' | 'Female' | null;  // ⭐ NOUVEAU - Genre de l'étudiant
  student_label?: string | null;                // ⭐ NOUVEAU - Label calculé ("الطالب" ou "الطالبة")
  is_reply?: boolean; // Indique si c'est une réponse
  reply_to_message_id?: number | null; // ID du message auquel on répond
}

interface ParentStats {
  avgHifz: number;
  avgGrades: number;
  subjectsCount: number;
  messagesCount: number;
  totalStudents?: number;
  averageGrades?: number;
  averageHifz?: number;
  totalSubjects?: number;
  unreadMessages?: number;
}

const ParentDashboard: React.FC<ParentDashboardProps> = ({ onBackHome, onAuthError, currentLanguage = 'ar', translations }) => {
  const { association, getName } = useAssociation();
  const logoUrl = association?.logoUrl ?? '/images/logo.png';
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeSection, setActiveSection] = useState('userHome');
  const [parentProfile, setParentProfile] = useState<any>(null);
  const [linkedStudents, setLinkedStudents] = useState<LinkedStudent[]>([]);
  const isLoadingRef = useRef(false);
  const [grades, setGrades] = useState<Grade[]>([]);
  const [quranRecords, setQuranRecords] = useState<QuranRecord[]>([]);
  const [messages, setMessages] = useState<Message[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [stats, setStats] = useState<ParentStats>({
    avgHifz: 0,
    avgGrades: 0,
    subjectsCount: 0,
    messagesCount: 0
  });
  const [recentMessages, setRecentMessages] = useState<Message[]>([]);
  const [statsLoadedFromBackend, setStatsLoadedFromBackend] = useState(false);
  const [showSendMessageModal, setShowSendMessageModal] = useState(false);
  const [showReplyMessageModal, setShowReplyMessageModal] = useState(false);
  const [messageToReplyTo, setMessageToReplyTo] = useState<Message | null>(null);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [parentMessage, setParentMessage] = useState({
    studentId: '',
    sendToAdmin: false, // Checkbox pour "الإدارة"
    sendToTeachers: false, // Checkbox pour "الأساتذة"
    subject: '',
    message: ''
  });

  const apiService = ApiServiceParent.getInstance();
  const styles = GLOBAL_STYLES.dashboard;

  // Enregistrer le handler d'erreur d'authentification
  useEffect(() => {
    if (onAuthError && apiService && typeof apiService.setAuthErrorHandler === 'function') {
      apiService.setAuthErrorHandler(onAuthError);
    }
  }, [onAuthError, apiService]);

  // États pour la responsivité
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1024);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [selectedStudentFilter, setSelectedStudentFilter] = useState<string>('all'); // Filtre pour les enfants
  const [refreshing, setRefreshing] = useState<string | null>(null);
  const [activeMessageTab, setActiveMessageTab] = useState<'received' | 'sent'>('received'); // Onglet actif pour les messages
  const [expandedMessageId, setExpandedMessageId] = useState<number | null>(null);
  const [targetRepeatNewHifzCount, setTargetRepeatNewHifzCount] = useState<number>(15); // ✅ Paramètre pour le badge de répétition (défaut: 15)

  // Mémoriser les tableaux pour éviter les re-renders inutiles
  const memoizedStudents = useMemo(() => {
    return linkedStudents.map(s => ({
      id: s.studentId || s.id,
      student_name: s.student_name,
      studentName: s.student_name,
      level: s.class_name || '',
      hizbCount: 0,
      gender: s.gender // Inclure gender pour le partage de code
    }));
  }, [linkedStudents]);

  const memoizedQuranRecords = useMemo(() => {
    return quranRecords.map(r => ({
      ...r,
      studentId: r.studentId,
      studentName: r.studentName,
      teacherId: 0
    })) as QuranRecord[];
  }, [quranRecords]);

  // Gestion du redimensionnement de la fenêtre
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      // Fermer le menu mobile si on passe en mode desktop
      if (window.innerWidth >= 768) {
        setIsMobileMenuOpen(false);
      }
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Charger le paramètre targetRepeatNewHifzCount depuis l'API
  useEffect(() => {
    const fetchTargetRepeatNewHifzCount = async () => {
      try {
        const apiServiceAdmin = ApiServiceAdmin.getInstance();
        const response = await apiServiceAdmin.getParameterByName('targetRepeatNewHifzCount');
        if (response.success && response.data) {
          const count = parseInt(response.data.value) || 15;
          setTargetRepeatNewHifzCount(count);
          console.log('✅ ParentDashboard - Paramètre targetRepeatNewHifzCount chargé:', count);
        }
      } catch (error) {
        console.error('Erreur lors du chargement du paramètre targetRepeatNewHifzCount:', error);
        // Garder la valeur par défaut de 15
      }
    };
    
    fetchTargetRepeatNewHifzCount();
  }, []);

  // Fonction de traduction
  const t = (key: string): string => {
    if (!translations) return key;
    const lang = (currentLanguage === 'ar' || currentLanguage === 'fr' || currentLanguage === 'en') ? currentLanguage : 'ar';
    return translations[lang]?.[key] || key;
  };

  const isRTL = currentLanguage === 'ar';
  const parentApiLang = currentLanguage === 'fr' || currentLanguage === 'en' ? currentLanguage : 'ar';

  // Fonction pour traduire le type de rapport
  const translateReportType = (subject: string): string => {
    if (!subject) return subject;
    
    // Normaliser le sujet en minuscules pour la comparaison
    const normalizedSubject = subject.toLowerCase().trim();
    
    // Gérer les cas où le sujet commence par "Rapport" ou contient le type
    if (normalizedSubject.includes('rapport') || normalizedSubject.includes('report')) {
      if (normalizedSubject.includes('assignment') || normalizedSubject.includes('واجب') || normalizedSubject.includes('devoir')) {
        return t('reportTypeAssignment');
      }
      if (normalizedSubject.includes('progress') || normalizedSubject.includes('تقدم')) {
        return t('reportTypeProgress');
      }
      if (normalizedSubject.includes('achievement') || normalizedSubject.includes('إنجاز') || normalizedSubject.includes('réussite')) {
        return t('reportTypeAchievement');
      }
      if (normalizedSubject.includes('concern') || normalizedSubject.includes('ملاحظة') || normalizedSubject.includes('note')) {
        return t('reportTypeConcern');
      }
    }
    
    // Mapping direct pour les types purs
    const reportTypeMap: { [key: string]: string } = {
      'assignment': t('reportTypeAssignment'),
      'progress': t('reportTypeProgress'),
      'achievement': t('reportTypeAchievement'),
      'concern': t('reportTypeConcern'),
      'واجب منزلي': t('reportTypeAssignment'),
      'تقرير تقدم': t('reportTypeProgress'),
      'إنجاز متميز': t('reportTypeAchievement'),
      'ملاحظة مهمة': t('reportTypeConcern'),
      'rapport assignment': t('reportTypeAssignment'),
      'rapport progress': t('reportTypeProgress'),
      'rapport achievement': t('reportTypeAchievement'),
      'rapport concern': t('reportTypeConcern')
    };
    
    // Chercher une correspondance exacte
    if (reportTypeMap[normalizedSubject] || reportTypeMap[subject]) {
      return reportTypeMap[normalizedSubject] || reportTypeMap[subject];
    }
    
    // Si aucune correspondance, retourner le sujet original
    return subject;
  };

  // Fonction pour traduire la priorité
  const translatePriority = (priority: string): string => {
    const priorityMap: { [key: string]: string } = {
      'normal': t('priorityNormal'),
      'high': t('priorityHigh'),
      'urgent': t('priorityUrgent'),
      'عادية': t('priorityNormal'),
      'عالية': t('priorityHigh'),
      'عاجلة': t('priorityUrgent')
    };
    return priorityMap[priority] || priority;
  };

  // Fonction pour obtenir le titre de l'expéditeur
  const getSenderTitle = (senderType: string | null | undefined): string => {
    if (!senderType) return '';
    const type = senderType.toLowerCase();
    if (type === 'teacher' || type === 'معلم' || type === 'professor' || type === 'أستاذ') return t('senderTeacher');
    if (type === 'admin' || type === 'مدير' || type === 'administrator') return t('senderAdmin');
    if (type === 'parent' || type === 'ولي الأمر') return t('senderParent');
    return '';
  };

  // Mettre à jour les statistiques
  const updateStats = useCallback(() => {
    const studentsLength = linkedStudents.length;
    const messagesLength = messages.length;
    
    if (studentsLength === 0) {
      setStats({
        avgHifz: 0,
        avgGrades: 0,
        subjectsCount: 0,
        messagesCount: messagesLength
      });
      return;
    }

    // Calculer la moyenne Hifz
    let totalHifz = 0;
    let hifzCount = 0;
    linkedStudents.forEach(student => {
      const studentRecords = quranRecords.filter(r => r.studentId === student.id);
      const sortedRecords = studentRecords.sort((a, b) => {
        const dateA = new Date(a.modifiedDate || a.createdDate || 0).getTime();
        const dateB = new Date(b.modifiedDate || b.createdDate || 0).getTime();
        return dateB - dateA;
      });
      const latestRecord = sortedRecords[0];
      if (latestRecord) {
        totalHifz += latestRecord.totalHizb || 0;
        hifzCount++;
      }
    });
    const avgHifz = hifzCount > 0 ? totalHifz / hifzCount : 0;

    // Calculer la moyenne des notes
    let totalAverage = 0;
    if (grades.length > 0) {
      totalAverage = grades.reduce((sum, grade) => sum + grade.grade, 0) / grades.length;
    }

    // Compter les matières uniques
    const uniqueSubjects = Array.from(new Set(grades.map((g: Grade) => g.subject)));

    setStats({
      avgHifz: Math.round(avgHifz * 10) / 10,
      avgGrades: Math.round(totalAverage * 10) / 10,
      subjectsCount: uniqueSubjects.length,
      messagesCount: messagesLength
    });
  }, [linkedStudents, quranRecords, grades, messages]);

  // Fonction pour rafraîchir les notes
  const refreshGrades = async () => {
    setRefreshing('grades');
    try {
      const gradesResponse = await apiService.getParentGrades(parentApiLang);
      if (gradesResponse.success && gradesResponse.data) {
        const linkedGrades = gradesResponse.data.map((grade: any) => ({
          id: grade.id,
          student_id: grade.student_id,
          student_name: GLOBAL_UTILS.cleanText(grade.student_name || 'غير محدد'),
          subject: GLOBAL_UTILS.cleanText(grade.subject_name || grade.subject || ''),
          grade: grade.grade || 0,
          date: grade.date || grade.created_date || '',
          comment: GLOBAL_UTILS.cleanText(grade.comment || ''),
          teacher_name: GLOBAL_UTILS.cleanText(grade.teacher_name || '')
        }));
        setGrades(linkedGrades);
        console.log('✅ PARENT DASHBOARD - Notes rafraîchies:', linkedGrades.length);
      }
    } catch (error) {
      console.error('Erreur lors du rafraîchissement des notes:', error);
    } finally {
      setRefreshing(null);
    }
  };

  // Fonction pour rafraîchir les enregistrements Coran (compatible avec QuranTrackingSection)
  const refreshQuranRecords = useCallback(async (): Promise<boolean> => {
    console.log('🔄 PARENT DASHBOARD - refreshQuranRecords - DÉBUT');
    try {
      // 1. Récupérer les enregistrements Coran (sans les données ns_*)
      console.log('🔄 PARENT DASHBOARD - refreshQuranRecords - Appel API getParentQuranRecords...');
      const quranResponse = await apiService.getParentQuranRecords(true);
      console.log('🔄 PARENT DASHBOARD - refreshQuranRecords - Réponse API:', {
        success: quranResponse.success,
        dataLength: quranResponse.data?.length,
        hasData: !!quranResponse.data
      });
      if (!quranResponse.success || !quranResponse.data) {
        return false;
      }

      // 2. Extraire les IDs uniques des étudiants
      const uniqueStudentIds = Array.from(new Set(quranResponse.data.map((r: any) => r.studentId || r.student_id).filter(Boolean)));
      console.log('🔄 PARENT DASHBOARD - refreshQuranRecords - IDs étudiants uniques:', uniqueStudentIds);
      
      // 3. Récupérer les sessions suivantes pour chaque étudiant en parallèle
      console.log('🔄 PARENT DASHBOARD - refreshQuranRecords - Début récupération sessions suivantes...');
      const nextSessionsPromises = uniqueStudentIds.map(studentId => {
        console.log(`🔄 PARENT DASHBOARD - refreshQuranRecords - Appel getNextSessionForStudent(${studentId})...`);
        return apiService.getNextSessionForStudent(studentId).catch((error: any) => {
          console.warn(`⚠️ PARENT DASHBOARD - Erreur lors de la récupération de la session suivante pour studentId ${studentId}:`, error);
          return { success: false, data: null, error: error?.message || 'Erreur inconnue' };
        });
      });
      
      const nextSessionsResults = await Promise.all(nextSessionsPromises);
      console.log('🔄 PARENT DASHBOARD - refreshQuranRecords - Résultats sessions suivantes:', nextSessionsResults);
      
      // 4. Créer un Map pour un accès rapide (studentId -> nextSession)
      const nextSessionMap = new Map();
      nextSessionsResults.forEach((result, index) => {
        const studentId = uniqueStudentIds[index];
        if (result.success && result.data) {
          console.log(`✅ PARENT DASHBOARD - Session suivante récupérée pour studentId ${studentId}:`, result.data);
          nextSessionMap.set(studentId, result.data);
        } else {
          console.log(`⚠️ PARENT DASHBOARD - Aucune session suivante pour studentId ${studentId}:`, result.error || 'Pas de données');
        }
      });

      // 5. Combiner les données : mapper les enregistrements Coran avec leurs sessions suivantes
      const linkedQuranRecords: QuranRecord[] = quranResponse.data.map((record: any) => {
        const studentId = record.studentId || record.student_id;
        const nextSession = nextSessionMap.get(studentId) || null;
        
        // Debug: vérifier la structure de nextSession
        if (nextSession) {
          console.log(`🔍 PARENT DASHBOARD - nextSession pour studentId ${studentId}:`, {
            nextSession,
            keys: Object.keys(nextSession),
            nextHalaqaNewHifzText: nextSession.nextHalaqaNewHifzText,
            nextHalaqaOldHifzMurajahText: nextSession.nextHalaqaOldHifzMurajahText,
            nextHalaqaNewHifzMurajahText: nextSession.nextHalaqaNewHifzMurajahText
          });
        }
        
        // Utiliser la fonction de mapping centralisée
        const mapped = mapQuranRecordFromBackend(record, nextSession);
        // Ajouter les champs supplémentaires nécessaires pour ParentDashboard
        return {
          ...mapped,
          student_id: record.student_id,
          student_name: mapped.studentName, // Pour compatibilité avec l'ancien code
          teacher_name: GLOBAL_UTILS.cleanText(record.teacher_name || ''),
          teacherId: record.teacherId || 0,
          level: record.level || '',
          startPoint: record.startPoint || 'الناس',
          newHifzRange: record.newHifzRange || null,
          oldHifzMurajahRange: record.oldHifzMurajahRange || null,
          newHifzMurajahRange: record.newHifzMurajahRange || null
        };
      });
      
      setQuranRecords(linkedQuranRecords);
      console.log('✅ PARENT DASHBOARD - Enregistrements Coran rafraîchis:', linkedQuranRecords.length);
      console.log('🔍 PARENT DASHBOARD - Sessions suivantes récupérées:', nextSessionMap.size);
      console.log('🔍 PARENT DASHBOARD - IDs étudiants uniques:', uniqueStudentIds);
      console.log('🔍 PARENT DASHBOARD - Résultats sessions suivantes:', nextSessionsResults);
      if (linkedQuranRecords.length > 0) {
        console.log('🔍 PARENT DASHBOARD - Premier enregistrement après refresh:', {
          id: linkedQuranRecords[0].id,
          studentId: linkedQuranRecords[0].studentId,
          nextHalaqaNewHifzText: linkedQuranRecords[0].nextHalaqaNewHifzText,
          nextHalaqaOldHifzMurajahText: linkedQuranRecords[0].nextHalaqaOldHifzMurajahText,
          nextHalaqaNewHifzMurajahText: linkedQuranRecords[0].nextHalaqaNewHifzMurajahText,
          repeatNewHifz: linkedQuranRecords[0].repeatNewHifz,
          nextSessionRepeatNewHifz: linkedQuranRecords[0].nextSessionRepeatNewHifz
        });
      }
      return true;
    } catch (error) {
      console.error('Erreur lors du rafraîchissement des enregistrements Coran:', error);
      return false;
    }
  }, [apiService]);

  // Fonction pour mettre à jour repeatNewHifz dans quran_next_session
  const handleUpdateRepeatNewHifzInNextSession = useCallback(async (studentId: number, newValue: number): Promise<void> => {
    try {
      const response = await apiService.updateRepeatNewHifzInNextSession(studentId, newValue);
      
      if (response.success) {
        // Rafraîchir les enregistrements Coran pour afficher la nouvelle valeur
        await refreshQuranRecords();
      } else {
        throw new Error(response.error || 'Erreur lors de la mise à jour');
      }
    } catch (error) {
      console.error('❌ PARENT DASHBOARD - Erreur lors de la mise à jour de repeatNewHifz:', error);
      throw error; // Propager l'erreur pour que le composant puisse la gérer
    }
  }, [apiService, refreshQuranRecords]);


  // Callback pour récupérer la session suivante d'un étudiant
  const handleGetNextSessionForStudent = useCallback(async (studentId: number) => {
    return await apiService.getNextSessionForStudent(studentId);
  }, [apiService]);

  // Fonction helper pour mapper les messages avec les informations de l'étudiant
  // Utilisation d'une fonction normale au lieu de useCallback pour éviter les dépendances circulaires
  const mapMessagesWithStudent = (messagesData: any[], studentsList: LinkedStudent[]): Message[] => {
    return messagesData.map((message: any) => {
      let studentName = null;
      let cleanContent = GLOBAL_UTILS.cleanText(message.content || '');
      
      // Vérifier d'abord si student_id est disponible dans la réponse
      if (message.student_id) {
        const student = studentsList.find(s => s.id === message.student_id || s.studentId === message.student_id);
        studentName = student ? student.student_name : null;
      }
      
      // Si pas de student_id, essayer d'extraire depuis le contenu (format: "الطالب/الطالبة: [Nom]\n\n[Contenu]")
      if (!studentName) {
        // Chercher le pattern avec les différentes formes inclusives
        const studentLabelPatterns = [
          /الطالب\/ـة:\s*([^\n]+)/i,       // Arabe: الطالب/ـة (nouveau format)
          /الطالب\/الطالبة:\s*([^\n]+)/i,  // Arabe: الطالب/الطالبة (ancien format)
          /الطالب\(ة\):\s*([^\n]+)/i,      // Arabe: الطالب(ة) (ancien format)
          /الطالب:\s*([^\n]+)/i,           // Arabe: الطالب (ancien format)
          /Student\(s\):\s*([^\n]+)/i,     // Anglais: Student(s)
          /Student:\s*([^\n]+)/i,          // Anglais: Student (ancien format)
          /Étudiant\(e\):\s*([^\n]+)/i,    // Français: Étudiant(e)
          /Étudiant:\s*([^\n]+)/i          // Français: Étudiant (ancien format)
        ];
        
        for (const pattern of studentLabelPatterns) {
          const match = cleanContent.match(pattern);
          if (match && match[1]) {
            const potentialStudentName = match[1].trim();
            // Vérifier si ce nom correspond à un étudiant lié
            const matchingStudent = studentsList.find(s => {
              const studentNameClean = GLOBAL_UTILS.cleanText(s.student_name || '');
              return studentNameClean === potentialStudentName || 
                     studentNameClean.includes(potentialStudentName) ||
                     potentialStudentName.includes(studentNameClean);
            });
            if (matchingStudent) {
              studentName = matchingStudent.student_name;
              // Retirer le nom de l'étudiant du contenu pour l'affichage
              cleanContent = cleanContent.replace(pattern, '').trim();
              // Retirer les sauts de ligne multiples au début
              cleanContent = cleanContent.replace(/^\n+/, '').trim();
              break;
            }
          }
        }
      }
      
      // Fallback: utiliser student_name directement depuis la réponse si disponible
      if (!studentName && message.student_name) {
        studentName = GLOBAL_UTILS.cleanText(message.student_name);
      }
      
      return {
        id: message.id,
        subject: GLOBAL_UTILS.cleanText(message.subject || 'غير محدد'),
        content: cleanContent,
        sender_name: GLOBAL_UTILS.cleanText(message.sender_name || message.sender_full_name || 'غير محدد'),
        sender_type: message.sender_type || message.type || null,
        sent_date: message.sent_date || message.created_date || '',
        sent_time: message.sent_time || '',
        priority: (message.priority || 'normal') as 'normal' | 'high' | 'urgent',
        is_read: message.is_read === 1 || message.is_read === true,
        student_id: message.student_id || null,
        student_name: studentName,
        // student_gender et student_label sont maintenant inclus dans la réponse du backend
        student_gender: message.student_gender || null,
        student_label: message.student_label || null,
        // Mapper reply_to_message_id depuis l'API
        is_reply: message.reply_to_message_id !== null && message.reply_to_message_id !== undefined,
        reply_to_message_id: message.reply_to_message_id || null
      };
    });
  };

  // Fonction pour rafraîchir les messages
  const refreshMessages = async () => {
    setRefreshing('messages');
    try {
      const messagesResponse = await apiService.getParentMessages();
      if (messagesResponse.success && messagesResponse.data) {
        // Le genre est maintenant retourné directement par l'API dans les étudiants
        // Plus besoin d'extraire depuis le contenu des messages
        const allMessages = mapMessagesWithStudent(messagesResponse.data, linkedStudents);
        setMessages(allMessages);
        setRecentMessages(allMessages.slice(0, 5));
        console.log('✅ PARENT DASHBOARD - Messages rafraîchis:', allMessages.length);
      }
    } catch (error) {
      console.error('Erreur lors du rafraîchissement des messages:', error);
    } finally {
      setRefreshing(null);
    }
  };

  // Charger les données du dashboard
  const loadDashboardData = useCallback(async () => {
    // Éviter les appels multiples simultanés
    if (isLoadingRef.current) {
      console.log('⏸️ PARENT DASHBOARD - loadDashboardData déjà en cours, ignoré');
      return;
    }
    
    try {
      isLoadingRef.current = true;
      setLoading(true);
      setError(null);

      // Récupérer le profil du parent connecté
      const profileResponse = await apiService.getParentProfile();
      if (profileResponse.success && profileResponse.data) {
        setParentProfile(profileResponse.data);
        console.log('✅ PARENT DASHBOARD - Profil parent chargé:', profileResponse.data);
      } else {
        // Fallback vers getCurrentUser si le profil API échoue
      const currentUser = apiService.getCurrentUser();
      if (currentUser) {
        setParentProfile(currentUser);
          console.log('✅ PARENT DASHBOARD - Profil parent chargé (fallback):', currentUser);
        }
      }

      // Récupérer les étudiants du parent via la route dédiée
      const studentsResponse = await apiService.getParentStudents();
      let currentLinkedStudents: LinkedStudent[] = [];
      if (studentsResponse.success && studentsResponse.data) {
        const linkedStudentsData = studentsResponse.data.map((student: any) => ({
          id: student.student_id || student.id,
          studentId: student.student_id || student.id, // DTO camelCase
          student_name: GLOBAL_UTILS.cleanText(student.student_name || student.name || 'غير محدد'),
          class_name: GLOBAL_UTILS.cleanText(student.level || student.class_name || ''),
          halqa: GLOBAL_UTILS.cleanText(student.halqa || ''),
          // Le genre est maintenant retourné directement par l'API
          gender: student.gender || undefined
        }));
        
        currentLinkedStudents = linkedStudentsData;
        setLinkedStudents(linkedStudentsData);
        console.log('✅ PARENT DASHBOARD - Étudiants chargés:', linkedStudentsData.length);
        console.log('🔍 PARENT DASHBOARD - Détails des étudiants:', linkedStudentsData.map((s: LinkedStudent) => ({ id: s.id, studentId: s.studentId, student_name: s.student_name })));
      } else {
        console.error('❌ PARENT DASHBOARD - Erreur lors du chargement des étudiants:', studentsResponse.error);
        setLinkedStudents([]);
      }

      // Récupérer les notes des enfants via la route dédiée
      const gradesResponse = await apiService.getParentGrades(parentApiLang);
                if (gradesResponse.success && gradesResponse.data) {
        const linkedGrades = gradesResponse.data.map((grade: any) => ({
                      id: grade.id,
                      student_id: grade.student_id,
          student_name: GLOBAL_UTILS.cleanText(grade.student_name || 'غير محدد'),
          subject: GLOBAL_UTILS.cleanText(grade.subject_name || grade.subject || ''),
          grade: grade.grade || 0,
                      date: grade.date || grade.created_date || '',
          comment: GLOBAL_UTILS.cleanText(grade.comment || ''),
          teacher_name: GLOBAL_UTILS.cleanText(grade.teacher_name || '')
                    }));
                  setGrades(linkedGrades);
                  console.log('✅ PARENT DASHBOARD - Notes chargées:', linkedGrades.length);
      } else {
        console.error('❌ PARENT DASHBOARD - Erreur lors du chargement des notes:', gradesResponse.error);
        setGrades([]);
                }

      // ✅ Récupérer les enregistrements Coran avec les sessions suivantes (utilise la nouvelle logique)
      console.log('🔄 PARENT DASHBOARD - loadDashboardData - Appel refreshQuranRecords...');
      await refreshQuranRecords();

      // Récupérer les statistiques via la route dédiée
      const statsResponse = await apiService.getParentStats();
      if (statsResponse.success && statsResponse.data) {
        const backendStats = statsResponse.data;
        // Utiliser les valeurs du backend, pas les valeurs locales qui peuvent être obsolètes
        setStats({
          avgHifz: backendStats.averageHifz || 0,
          avgGrades: backendStats.averageGrades || 0,
          subjectsCount: backendStats.totalSubjects || 0,
          messagesCount: backendStats.unreadMessages || 0,
          totalStudents: backendStats.totalStudents || 0,
          averageGrades: backendStats.averageGrades,
          averageHifz: backendStats.averageHifz,
          totalSubjects: backendStats.totalSubjects,
          unreadMessages: backendStats.unreadMessages
        });
        setStatsLoadedFromBackend(true);
        console.log('✅ PARENT DASHBOARD - Statistiques chargées depuis le backend:', backendStats);
      } else {
        console.error('❌ PARENT DASHBOARD - Erreur lors du chargement des statistiques:', statsResponse.error);
        setStatsLoadedFromBackend(false);
        // Utiliser updateStats() pour calculer localement si l'API échoue
        // updateStats sera appelé via useEffect après le chargement des données
      }

      // Récupérer les messages via la route dédiée
      const receiveLogStart = {
        action: 'receive_messages',
        component: 'ParentDashboard',
        timestamp: new Date().toISOString(),
        route: '/api/parent/messages',
        method: 'GET'
      };
      console.log('📥 PARENT DASHBOARD - loadDashboardData - Début réception:', JSON.stringify(receiveLogStart, null, 2));
      
      const messagesResponse = await apiService.getParentMessages();
      
      const receiveLogResponse = {
        action: 'receive_messages',
        component: 'ParentDashboard',
        timestamp: new Date().toISOString(),
        response: {
          success: messagesResponse?.success || false,
          messages_count: messagesResponse?.data ? (Array.isArray(messagesResponse.data) ? messagesResponse.data.length : 0) : 0,
          error: messagesResponse?.error || null
        }
      };
      console.log('📥 PARENT DASHBOARD - loadDashboardData - Réponse:', JSON.stringify(receiveLogResponse, null, 2));
      
      if (messagesResponse.success && messagesResponse.data) {
        // Le genre est maintenant retourné directement par l'API dans les étudiants
        // Plus besoin d'extraire depuis le contenu des messages
        const allMessages = mapMessagesWithStudent(messagesResponse.data, currentLinkedStudents);

        setMessages(allMessages);
        // Prendre les 5 derniers messages pour recentMessages
        setRecentMessages(allMessages.slice(0, 5));
        
        const receiveLogSuccess = {
          action: 'receive_messages',
          component: 'ParentDashboard',
          timestamp: new Date().toISOString(),
          result: {
            messages_loaded: allMessages.length,
            recent_messages_count: allMessages.slice(0, 5).length,
            first_message_id: allMessages[0]?.id || null
          }
        };
        console.log('✅ PARENT DASHBOARD - loadDashboardData - Messages chargés:', JSON.stringify(receiveLogSuccess, null, 2));
      } else {
        const receiveLogError = {
          action: 'receive_messages',
          component: 'ParentDashboard',
          timestamp: new Date().toISOString(),
          error: messagesResponse?.error || 'Réponse invalide'
        };
        console.error('❌ PARENT DASHBOARD - loadDashboardData - Erreur:', JSON.stringify(receiveLogError, null, 2));
        setMessages([]);
        setRecentMessages([]);
      }

    } catch (error) {
      console.error('❌ PARENT DASHBOARD - Erreur lors du chargement:', error);
      setError('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
      isLoadingRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiService, refreshQuranRecords]);


  // Fonction pour envoyer un message depuis le parent
  const handleSendParentMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Sauvegarder si c'est une réponse avant de continuer
    const isReply = showReplyMessageModal && messageToReplyTo !== null;
    const replyToMessage = messageToReplyTo;
    
    // Validation : au moins une option doit être cochée
    if (!parentMessage.sendToAdmin && !parentMessage.sendToTeachers) {
      alert(t('selectAdminOrTeachers'));
      return;
    }
    
    // Si "الأساتذة" est cochée, l'étudiant est obligatoire
    const studentId = linkedStudents.length === 1 ? linkedStudents[0].id : parentMessage.studentId;
    if (parentMessage.sendToTeachers && !studentId) {
      alert(t('selectStudentForTeachers'));
      return;
    }
    
    // Log de début d'envoi (sera mis à jour après la recherche des profs)
    const sendLogStart = {
      action: 'send_message',
      component: 'ParentDashboard',
      timestamp: new Date().toISOString(),
      route: '/api/parent/send-message',
      method: 'POST',
      message_data: {
        sendToAdmin: parentMessage.sendToAdmin,
        sendToTeachers: parentMessage.sendToTeachers,
        studentId: studentId,
        subject: parentMessage.subject,
        message: parentMessage.message?.substring(0, 100) + (parentMessage.message?.length > 100 ? '...' : ''),
        message_length: parentMessage.message?.length || 0,
        linked_students_count: linkedStudents.length
      }
    };
    console.log('📤 PARENT DASHBOARD - handleSendParentMessage - Début:', JSON.stringify(sendLogStart, null, 2));
    
    try {
      // Déterminer recipient_type selon les sélections
      let recipientType: 'admin' | 'specific';
      let recipientIds: number[] = [];
      
      // Si "الأساتذة" est cochée, chercher les profs de l'étudiant
      if (parentMessage.sendToTeachers && studentId) {
        const teachersResponse = await apiService.getStudentTeachers(studentId);
        if (teachersResponse.success && Array.isArray(teachersResponse.data) && teachersResponse.data.length > 0) {
          teachersResponse.data.forEach((teacher: any) => {
            const teacherUserId = teacher.teacher_user_id || teacher.user_id || teacher.id;
            if (teacherUserId) {
              recipientIds.push(teacherUserId);
            }
          });
        } else {
          alert(t('noTeachersForStudent'));
          return;
        }
        
        if (recipientIds.length === 0) {
          alert(t('noTeachersForStudent'));
          return;
        }
      }
      
      // Déterminer le recipient_type
      if (parentMessage.sendToAdmin && parentMessage.sendToTeachers) {
        // Les deux sont cochés : envoyer aux admins et aux profs
        recipientType = 'admin'; // Le backend enverra aux admins + recipient_ids (profs)
      } else if (parentMessage.sendToAdmin) {
        // Seulement "الإدارة" : envoyer uniquement aux admins
        recipientType = 'admin';
      } else {
        // Seulement "الأساتذة" : envoyer uniquement aux profs
        recipientType = 'specific';
      }
      
      // Le nom de l'étudiant est maintenant affiché en haut, plus besoin de l'ajouter dans le contenu
      // Préparer les données pour l'envoi
      const messageData: any = {
        recipient_type: recipientType,
        subject: parentMessage.subject,
        message: parentMessage.message,
        priority: 'normal'
      };
      
      // Toujours envoyer studentId : utiliser l'ID de l'étudiant si disponible, sinon 0 pour indiquer qu'il n'y a pas d'étudiant spécifique
      // Le backend peut exiger ce champ même si on envoie uniquement à la direction
      messageData.studentId = studentId || 0;
      
      // Ajouter recipient_ids seulement s'il n'est pas vide (profs)
      if (recipientIds.length > 0) {
        messageData.recipient_ids = recipientIds;
      }
      
      const response = await apiService.sendParentMessage(messageData);
      
      // Log après réponse
      const sendLogAfter = {
        action: 'send_message',
        component: 'ParentDashboard',
        timestamp: new Date().toISOString(),
        response: {
          success: response.success,
          message_id: response.data?.id || null,
          recipients_count: response.data?.recipients_count || null,
          error: response.error || null
        }
      };
      console.log('📤 PARENT DASHBOARD - handleSendParentMessage - Après réponse:', JSON.stringify(sendLogAfter, null, 2));
      
      if (response.success) {
        alert(t('messageSent'));
        setParentMessage({
          studentId: '',
          sendToAdmin: false,
          sendToTeachers: false,
          subject: '',
          message: ''
        });
        
        // Fermer le bon modal (normal ou réponse)
        if (isReply) {
          setShowReplyMessageModal(false);
          setMessageToReplyTo(null);
        } else {
          setShowSendMessageModal(false);
        }
        
        // Recharger les messages
        const messagesResponse = await apiService.getParentMessages();
        if (messagesResponse.success && messagesResponse.data) {
          const allMessages = mapMessagesWithStudent(messagesResponse.data, linkedStudents);
          
          // Si c'est une réponse, marquer le nouveau message comme réponse
          if (isReply && replyToMessage) {
            // Trouver le message le plus récent dans les messages envoyés (celui qu'on vient d'envoyer)
            const sentMessages = allMessages.filter(msg => {
              const senderType = msg.sender_type?.toLowerCase() || '';
              return senderType === 'parent' || senderType === 'ولي الأمر';
            });
            if (sentMessages.length > 0) {
              // Le dernier message envoyé est probablement celui qu'on vient d'envoyer
              const latestSentMessage = sentMessages.sort((a, b) => {
                const dateA = new Date(a.sent_date + ' ' + (a.sent_time || ''));
                const dateB = new Date(b.sent_date + ' ' + (b.sent_time || ''));
                return dateB.getTime() - dateA.getTime();
              })[0];
              
              // Marquer comme réponse
              latestSentMessage.is_reply = true;
              latestSentMessage.reply_to_message_id = replyToMessage.id;
            }
          }
          
          // Utiliser reply_to_message_id depuis l'API pour déterminer si c'est une réponse
          // (reply_to_message_id est déjà mappé dans mapMessagesWithStudent)
          allMessages.forEach(msg => {
            if (msg.reply_to_message_id) {
              msg.is_reply = true;
            }
          });
          
          setMessages(allMessages);
          setRecentMessages(allMessages.slice(0, 5));
    }
      } else {
        alert(t('messageError') + ': ' + response.error);
      }
    } catch (error: any) {
      const sendLogError = {
        action: 'send_message',
        component: 'ParentDashboard',
        timestamp: new Date().toISOString(),
        exception: {
          message: error?.message || 'Erreur inconnue',
          type: error?.constructor?.name || 'Error'
        }
      };
      console.error('❌ PARENT DASHBOARD - handleSendParentMessage - Erreur:', JSON.stringify(sendLogError, null, 2));
      alert(t('messageError') + ': ' + error.message);
    }
  };

  const closeSendMessageModal = () => {
    setShowSendMessageModal(false);
    setParentMessage({
      studentId: '',
      sendToAdmin: false,
      sendToTeachers: false,
      subject: '',
      message: ''
    });
  };

  // Fonction pour ouvrir le modal de réponse
  const handleReplyToMessage = (message: Message) => {
    setMessageToReplyTo(message);
    setShowReplyMessageModal(true);
    
    // Pré-remplir les informations du message de réponse
    // Déterminer le destinataire selon le type d'expéditeur
    const senderType = message.sender_type?.toLowerCase() || '';
    let sendToAdmin = false;
    let sendToTeachers = false;
    
    if (senderType === 'admin' || senderType === 'administrator' || senderType === 'مدير') {
      sendToAdmin = true;
    } else if (senderType === 'teacher' || senderType === 'professor' || senderType === 'أستاذ') {
      sendToTeachers = true;
    } else {
      // Par défaut, envoyer à l'administration si on ne peut pas déterminer
      sendToAdmin = true;
    }
    
    // Préfixer le sujet avec un symbole de réponse (↩) et traduire le sujet
    let replySubject = message.subject || '';
    const translatedSubject = translateReportType(replySubject);
    // Utiliser le symbole ↩ au lieu de "رد:"
    if (!replySubject.toLowerCase().startsWith('re:') && !replySubject.toLowerCase().startsWith('↩') && !replySubject.startsWith('↩')) {
      replySubject = `↩ ${translatedSubject}`;
    } else {
      // Si déjà préfixé, remplacer juste le sujet par la version traduite
      replySubject = replySubject.replace(/^(رد:|re:|↩)\s*/i, '↩ ');
      replySubject = `↩ ${translatedSubject}`;
    }
    
    // Construire le message avec les informations du message original formatées (style Gmail)
    // Format: 3 lignes vides + informations avec préfixes "> " + contenu du message
    const studentLabel = message.student_label || 
      GLOBAL_UTILS.getStudentLabel(message.student_gender) ||
      (message.student_id ? GLOBAL_UTILS.getStudentLabel(linkedStudents.find(s => s.id === message.student_id || s.studentId === message.student_id)?.gender) : 'الطالب/ـة');
    
    // Construire les lignes du message formaté
    const messageLines: string[] = [
      '', // Ligne vide 1
      '', // Ligne vide 2
      '', // Ligne vide 3
      `> ${t('from')}: ${message.sender_name}${message.sender_type ? ` (${getSenderTitle(message.sender_type)})` : ''}`
    ];
    
    // Ajouter l'étudiant si disponible
    if (message.student_name) {
      messageLines.push(`> ${studentLabel}: ${message.student_name}`);
    }
    
    // Ajouter le sujet (traduit)
    messageLines.push(`> ${t('messageSubject')}: ${translateReportType(message.subject)}`);
    
    // Ajouter la date
    messageLines.push(`> ${t('date')}: ${GLOBAL_UTILS.formatDate(message.sent_date)}${message.sent_time ? ` ${message.sent_time}` : ''}`);
    
    // Ligne vide avant le contenu
    messageLines.push('>');
    
    // Ajouter le contenu du message avec préfixes "> "
    const contentLines = message.content.split('\n').map((line) => `> ${line}`);
    messageLines.push(...contentLines);
    
    const formattedOriginalMessage = messageLines.join('\n');
    
    // Si c'est une réponse à un professeur, sendToTeachers doit toujours être true
    const finalSendToTeachers = sendToTeachers || (message.sender_type?.toLowerCase() === 'teacher' || message.sender_type?.toLowerCase() === 'أستاذ');
    
    setParentMessage({
      studentId: message.student_id ? String(message.student_id) : '',
      sendToAdmin: sendToAdmin,
      sendToTeachers: finalSendToTeachers,
      subject: replySubject,
      message: formattedOriginalMessage
    });
  };

  const closeReplyMessageModal = () => {
    setShowReplyMessageModal(false);
    setMessageToReplyTo(null);
    setParentMessage({
      studentId: '',
      sendToAdmin: false,
      sendToTeachers: false,
      subject: '',
      message: ''
    });
  };

  // Fonction pour gérer le clic sur un message (expansion/réduction)
  const handleMessageClick = (messageId: number, e?: React.MouseEvent) => {
    // Empêcher la propagation si on clique sur le bouton de réponse
    if (e && (e.target as HTMLElement).closest('button')) {
      return;
    }
    
    if (expandedMessageId === messageId) {
      // Si on clique sur le même message, le fermer
      setExpandedMessageId(null);
    } else {
      // Sinon, ouvrir le nouveau message (ferme automatiquement l'ancien)
      setExpandedMessageId(messageId);
    }
  };

  // Fonction pour extraire le texte sans HTML (style AdminDashboard)
  // Pour l'aperçu (une seule ligne)
  const getPlainText = (content: string | null | undefined, maxLength: number = 80): string => {
    if (!content) return 'لا يوجد محتوى';
    
    // Supprimer tous les préfixes "> " ou "< " (y compris multiples comme ">>") de chaque ligne
    let text = content.split('\n').map(line => {
      // Supprimer tous les ">" et espaces au début de la ligne
      let cleaned = line.replace(/^[>\s]+/, '');
      // Supprimer aussi les "<" et espaces au début
      cleaned = cleaned.replace(/^[<\s]+/, '');
      return cleaned.trim();
    }).join(' ').trim();
    
    // Nettoyer les espaces multiples
    text = text.replace(/\s+/g, ' ').trim();
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  // Fonction pour garder le contenu tel quel avec les préfixes et les retours à la ligne (pour l'affichage complet)
  const getCleanedContent = (content: string | null | undefined): string => {
    if (!content) return 'لا يوجد محتوى';
    
    // Normaliser les retours à la ligne (Windows \r\n -> \n, Mac \r -> \n) pour garantir un affichage correct
    // Préserver tous les préfixes "> ", ">>", "> >", etc. et les retours à la ligne
    return content.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  };


  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  // Mettre à jour les statistiques localement seulement si elles n'ont pas été chargées depuis le backend
  // (fallback si l'API stats échoue)
  // Utiliser une ref pour éviter les boucles de dépendances
  const updateStatsRef = useRef(updateStats);
  updateStatsRef.current = updateStats;
  
  useEffect(() => {
    // Ne calculer les stats localement que si elles n'ont pas été chargées depuis le backend
    if (!statsLoadedFromBackend && (linkedStudents.length > 0 || grades.length > 0 || quranRecords.length > 0)) {
      updateStatsRef.current();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkedStudents.length, quranRecords.length, grades.length, messages.length, statsLoadedFromBackend]);

  // Callbacks mémorisés pour QuranTrackingSection pour éviter les re-renders inutiles
  const handleRefreshStats = useCallback(async () => {
    // Forcer le rechargement complet de toutes les données lors du refresh
    try {
      // Recharger les étudiants
      const studentsResponse = await apiService.getParentStudents(true);
      if (studentsResponse.success && studentsResponse.data) {
        const linkedStudentsData = studentsResponse.data.map((student: any) => ({
          id: student.student_id || student.id,
          student_id: student.student_id || student.id,
          student_name: GLOBAL_UTILS.cleanText(student.student_name || student.name || 'غير محدد'),
          class_name: GLOBAL_UTILS.cleanText(student.level || student.class_name || ''),
          halqa: GLOBAL_UTILS.cleanText(student.halqa || ''),
          gender: student.gender
        }));
        setLinkedStudents(linkedStudentsData);
        console.log('🔄 PARENT DASHBOARD - Étudiants rechargés lors du refresh:', linkedStudentsData.length);
      }
      
      // Recharger les statistiques
      await updateStats();
    } catch (error) {
      console.error('❌ PARENT DASHBOARD - Erreur lors du refresh des données:', error);
      // En cas d'erreur, au moins mettre à jour les stats
      await updateStats();
    }
  }, [updateStats, apiService]);

  const handleGetStudents = useCallback(async () => {
    // Forcer le rechargement complet sans cache
    const response = await apiService.getParentStudents(true);
    if (response.success && response.data) {
      // Mettre à jour le state linkedStudents pour que memoizedStudents soit mis à jour
      const linkedStudentsData = response.data.map((student: any) => ({
        id: student.student_id || student.id,
        student_id: student.student_id || student.id,
        student_name: GLOBAL_UTILS.cleanText(student.student_name || student.name || 'غير محدد'),
        class_name: GLOBAL_UTILS.cleanText(student.level || student.class_name || ''),
        halqa: GLOBAL_UTILS.cleanText(student.halqa || ''),
        gender: student.gender
      }));
      setLinkedStudents(linkedStudentsData);
      console.log('🔄 PARENT DASHBOARD - handleGetStudents - Étudiants mis à jour:', linkedStudentsData.length);
      
      return {
        success: true,
        data: response.data.map((s: any) => ({
          id: s.student_id || s.id,
          student_name: s.student_name,
          studentName: s.student_name,
          level: s.level || s.class_name || '',
          gender: s.gender // Inclure gender pour le partage de code
        }))
      };
    }
    return { success: false };
  }, [apiService]);

  const handleGetQuranRecordById = useCallback(async () => {
    return { success: false, error: 'Not available for parents' };
  }, []);

  const handleAddQuranRecord = useCallback(async () => {
    return { success: false, error: 'Not available for parents' };
  }, []);

  const handleUpdateQuranRecord = useCallback(async () => {
    return { success: false, error: 'Not available for parents' };
  }, []);

  const handleRecordAdded = useCallback(async () => {
    await refreshQuranRecords();
    await updateStats();
  }, [refreshQuranRecords, updateStats]);

  const handleRecordUpdated = useCallback(async () => {
    await refreshQuranRecords();
    await updateStats();
  }, [refreshQuranRecords, updateStats]);

  // Navigation
  const getNavButtonStyle = (
    key: 'userHome' | 'myGrades' | 'quranProgress' | 'myMessages',
    isActive: boolean
  ) => {
    const palettes: Record<string, { activeBg: string; activeColor: string; borderColor: string }> = {
      userHome: { activeBg: '#e8f5e8', activeColor: '#2e7d32', borderColor: '#4caf50' },
      myGrades: { activeBg: '#e3f2fd', activeColor: '#1565c0', borderColor: '#2196f3' },
      quranProgress: { activeBg: '#e8f5e8', activeColor: '#2e7d32', borderColor: '#4caf50' },
      myMessages: { activeBg: '#f3e5f5', activeColor: '#7b1fa2', borderColor: '#9c27b0' }
    };
    const p = palettes[key];
    if (isActive) {
      return {
        backgroundColor: p.activeBg,
        color: p.activeColor,
        borderTop: 'none',
        borderLeft: 'none',
        borderRight: 'none',
        borderBottom: `2px solid ${p.borderColor}`
      } as React.CSSProperties;
    }
    return {
      backgroundColor: 'transparent',
      color: '#666666',
      borderTop: 'none',
      borderLeft: 'none',
      borderRight: 'none',
      borderBottom: '2px solid transparent'
    } as React.CSSProperties;
  };

  // Logout
  const handleLogout = () => {
    if (apiService && typeof apiService.logout === 'function') {
    apiService.logout();
    } else {
      // Fallback si la méthode logout n'est pas disponible
      localStorage.removeItem('app_token');
      localStorage.removeItem('app_user');
    }
    onBackHome();
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        background: 'linear-gradient(to bottom right, #e8f5e9, #c8e6c9)',
        fontFamily: 'Cairo, sans-serif'
      }}>
        <div style={{ textAlign: 'center', color: '#2E7D32', fontSize: '24px', fontWeight: 'bold' }}>
          <div>{t('loading')}</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        background: 'linear-gradient(to bottom right, #e8f5e9, #c8e6c9)',
        fontFamily: 'Cairo, sans-serif'
      }}>
        <div style={{ textAlign: 'center', color: '#D32F2F', fontSize: '24px', fontWeight: 'bold' }}>
          <div>{error}</div>
          <button
            onClick={loadDashboardData}
            style={{
              marginTop: '20px',
              padding: '12px 24px',
              backgroundColor: '#4CAF50',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '16px'
            }}
          >
            {t('retry')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(to bottom right, #f0fdf4, #dcfce7, #bbf7d0)',
      fontFamily: 'Cairo, sans-serif',
      direction: isRTL ? 'rtl' : 'ltr'
    }}>
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        /* Scrollbar personnalisée pour toutes les sections */
        .section-scrollable::-webkit-scrollbar {
          width: 13px;
        }
        .section-scrollable::-webkit-scrollbar-track {
          background: #f5f5f5;
          border-radius: 10px;
          border: 1px solid #e0e0e0;
        }
        .section-scrollable::-webkit-scrollbar-thumb {
          background: linear-gradient(180deg, #c1c1c1 0%, #a8a8a8 100%);
          border-radius: 10px;
          border: 1px solid #9a9a9a;
        }
        .section-scrollable::-webkit-scrollbar-thumb:hover {
          background: linear-gradient(180deg, #a8a8a8 0%, #909090 100%);
        }
        .section-scrollable::-webkit-scrollbar-thumb:active {
          background: linear-gradient(180deg, #909090 0%, #787878 100%);
        }
        /* Pour Firefox */
        .section-scrollable {
          scrollbar-width: auto;
          scrollbar-color: #c1c1c1 #f5f5f5;
        }
        /* Scrollbar personnalisée pour les sections de messages uniquement */
        .messages-container::-webkit-scrollbar {
          width: 8px;
        }
        .messages-container::-webkit-scrollbar-track {
          background: #f5f5f5;
          border-radius: 10px;
          border: 1px solid #e0e0e0;
        }
        .messages-container::-webkit-scrollbar-thumb {
          background: linear-gradient(180deg, #9c27b0 0%, #7b1fa2 100%);
          border-radius: 10px;
          border: 1px solid #6a1b9a;
        }
        .messages-container::-webkit-scrollbar-thumb:hover {
          background: linear-gradient(180deg, #7b1fa2 0%, #6a1b9a 100%);
        }
        .messages-container::-webkit-scrollbar-thumb:active {
          background: linear-gradient(180deg, #6a1b9a 0%, #4a148c 100%);
        }
        /* Pour Firefox */
        .messages-container {
          scrollbar-width: auto;
          scrollbar-color: #9c27b0 #f5f5f5;
        }
      `}</style>
      {/* Navigation */}
      <nav style={{
        ...styles.nav,
        backgroundColor: '#fafafa',
        borderBottom: '1px solid #e0e0e0',
        marginBottom: '30px',
        padding: 0
      }}>
        <div style={{
          ...styles.navContent,
          maxWidth: '1200px',
          padding: windowWidth < 768 ? '12px 15px' : '0 30px',
          height: windowWidth < 768 ? 'auto' : '70px',
          minHeight: windowWidth < 768 ? '60px' : '70px',
          display: 'flex',
          justifyContent: 'flex-start',
          alignItems: 'center',
          columnGap: windowWidth < 768 ? '12px' : '24px',
          flexWrap: windowWidth < 768 ? 'wrap' : 'nowrap'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexShrink: 0 }}>
            <div style={{
              width: windowWidth < 768 ? '50px' : '65px',
              height: windowWidth < 768 ? '50px' : '65px',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'white',
              overflow: 'hidden'
            }}>
              <img
                src={logoUrl}
                alt={getName(currentLanguage)}
                style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '6px' }}
              />
            </div>
            {windowWidth >= 768 && (
            <div style={{ marginLeft: '0' }}>
              <div style={{ 
                fontSize: '1.4rem', 
                fontWeight: '600', 
                color: '#1f2937',
                marginBottom: '2px',
                letterSpacing: '0.02em',
                lineHeight: '1.2'
              }}>
                {t('associationName')}
              </div>
              <div style={{ 
                fontSize: '0.95rem', 
                color: '#6b7280', 
                fontWeight: '400',
                marginTop: '0px',
                marginBottom: '2px',
                lineHeight: '1.3'
              }}>
                {t('parentBoardSubtitle')}
              </div>
              {parentProfile && (parentProfile.full_name || parentProfile.fullName) && (
                <div style={{ 
                  fontSize: '0.85rem', 
                  color: '#2E7D32', 
                  fontWeight: '500',
                  marginTop: '0px',
                  lineHeight: '1.2',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}>
                  {parentProfile.full_name || parentProfile.fullName}
                </div>
              )}
            </div>
            )}
          </div>

          {windowWidth < 768 ? (
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              style={{
                padding: '8px 12px',
                backgroundColor: 'transparent',
                border: '1px solid #e0e0e0',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '20px',
                marginLeft: 'auto'
              }}
            >
              ☰
            </button>
          ) : (
          <div style={{ 
            display: 'flex',
            gap: '2px',
            flexWrap: 'wrap' as const,
            alignItems: 'center'
          }}>
            <button
              onClick={() => setActiveSection('userHome')}
              style={{
                ...styles.navButton,
                ...getNavButtonStyle('userHome', activeSection === 'userHome'),
                padding: windowWidth < 1024 ? '10px 16px' : '12px 24px',
                fontSize: windowWidth < 1024 ? '14px' : '18px',
                borderRadius: '6px',
                fontWeight: '500',
                transition: 'all 0.2s ease',
                border: undefined // Supprimer border pour éviter le conflit avec borderBottom
              }}
            >
              {t('home')}
            </button>
            <button
              onClick={() => setActiveSection('myGrades')}
              style={{
                ...styles.navButton,
                ...getNavButtonStyle('myGrades', activeSection === 'myGrades'),
                padding: windowWidth < 1024 ? '10px 16px' : '12px 24px',
                fontSize: windowWidth < 1024 ? '14px' : '18px',
                borderRadius: '6px',
                fontWeight: '500',
                transition: 'all 0.2s ease',
                border: undefined
              }}
            >
              {t('myGrades')}
            </button>
            <button
              onClick={() => setActiveSection('quranProgress')}
              style={{
                ...styles.navButton,
                ...getNavButtonStyle('quranProgress', activeSection === 'quranProgress'),
                padding: windowWidth < 1024 ? '10px 16px' : '12px 24px',
                fontSize: windowWidth < 1024 ? '14px' : '18px',
                borderRadius: '6px',
                fontWeight: '500',
                transition: 'all 0.2s ease',
                border: undefined
              }}
            >
              {t('quranProgress')}
            </button>
            <button
              onClick={() => setActiveSection('myMessages')}
              style={{
                ...styles.navButton,
                ...getNavButtonStyle('myMessages', activeSection === 'myMessages'),
                padding: windowWidth < 1024 ? '10px 16px' : '12px 24px',
                fontSize: windowWidth < 1024 ? '14px' : '18px',
                borderRadius: '6px',
                fontWeight: '500',
                transition: 'all 0.2s ease',
                border: undefined
              }}
            >
              {t('myMessages')}
            </button>
            <button
              onClick={handleLogout}
              style={{
                ...styles.navButton,
                  padding: windowWidth < 1024 ? '10px 16px' : '12px 24px',
                  fontSize: windowWidth < 1024 ? '14px' : '18px',
                borderRadius: '6px',
                fontWeight: '500',
                transition: 'all 0.2s ease',
                backgroundColor: 'transparent',
                color: '#d32f2f',
                border: '1px solid #ffcdd2'
              }}
            >
              {t('logout')}
            </button>
          </div>
          )}
        </div>
        {/* Menu mobile */}
        {windowWidth < 768 && isMobileMenuOpen && (
          <div style={{
            width: '100%',
            padding: '15px',
            backgroundColor: 'white',
            borderTop: '1px solid #e0e0e0',
            display: 'flex',
            flexDirection: 'column',
            gap: '8px'
          }}>
            <button
              onClick={() => { setActiveSection('userHome'); setIsMobileMenuOpen(false); }}
              style={{
                ...styles.navButton,
                ...getNavButtonStyle('userHome', activeSection === 'userHome'),
                padding: '12px 16px',
                fontSize: '16px',
                borderRadius: '6px',
                fontWeight: '500',
                textAlign: isRTL ? 'right' : 'left',
                width: '100%',
                transition: 'all 0.2s ease',
                border: undefined
              }}
            >
              {t('home')}
            </button>
            <button
              onClick={() => { setActiveSection('myGrades'); setIsMobileMenuOpen(false); }}
              style={{
                ...styles.navButton,
                ...getNavButtonStyle('myGrades', activeSection === 'myGrades'),
                padding: '12px 16px',
                fontSize: '16px',
                borderRadius: '6px',
                fontWeight: '500',
                textAlign: isRTL ? 'right' : 'left',
                width: '100%',
                transition: 'all 0.2s ease',
                border: undefined
              }}
            >
              {t('myGrades')}
            </button>
            <button
              onClick={() => { setActiveSection('quranProgress'); setIsMobileMenuOpen(false); }}
              style={{
                ...styles.navButton,
                ...getNavButtonStyle('quranProgress', activeSection === 'quranProgress'),
                padding: '12px 16px',
                fontSize: '16px',
                borderRadius: '6px',
                fontWeight: '500',
                textAlign: isRTL ? 'right' : 'left',
                width: '100%',
                transition: 'all 0.2s ease',
                border: undefined
              }}
            >
              {t('quranProgress')}
            </button>
            <button
              onClick={() => { setActiveSection('myMessages'); setIsMobileMenuOpen(false); }}
              style={{
                ...styles.navButton,
                ...getNavButtonStyle('myMessages', activeSection === 'myMessages'),
                padding: '12px 16px',
                fontSize: '16px',
                borderRadius: '6px',
                fontWeight: '500',
                textAlign: isRTL ? 'right' : 'left',
                width: '100%',
                transition: 'all 0.2s ease',
                border: undefined
              }}
            >
              {t('myMessages')}
            </button>
            <div style={{ width: '100%', height: '1px', backgroundColor: '#e0e0e0', margin: '8px 0' }}></div>
            <button
              onClick={handleLogout}
              style={{
                ...styles.navButton,
                padding: '12px 16px',
                fontSize: '16px',
                borderRadius: '6px',
                fontWeight: '500',
                textAlign: isRTL ? 'right' : 'left',
                width: '100%',
                transition: 'all 0.2s ease',
                backgroundColor: 'transparent',
                color: '#d32f2f',
                border: '1px solid #ffcdd2'
              }}
            >
              {t('logout')}
            </button>
          </div>
        )}
      </nav>

      {/* Main Content */}
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: windowWidth < 768 ? '0 15px 20px' : windowWidth < 1024 ? '0 20px 30px' : '0 30px 30px'
      }}>
        {/* Home Section */}
        {activeSection === 'userHome' && (
          <div className="section-scrollable" style={{
            maxHeight: `calc(100vh - ${windowWidth < 768 ? '180px' : '200px'})`,
            overflowY: 'auto',
            overflowX: 'hidden',
            paddingRight: isRTL ? '0' : '8px',
            paddingLeft: isRTL ? '8px' : '0'
          }}>
            {/* Welcome Card */}
            <div style={{
              ...styles.card,
              marginBottom: windowWidth < 768 ? '24px' : '32px',
              padding: windowWidth < 768 ? '16px' : '24px'
            }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                flexDirection: windowWidth < 768 ? 'column' : 'row',
                gap: windowWidth < 768 ? '12px' : '0'
              }}>
                <div style={{ flex: 1, width: windowWidth < 768 ? '100%' : 'auto' }}>
                  <h1 style={{
                    fontSize: windowWidth < 768 ? '1.25rem' : '1.5rem',
                    fontWeight: 'bold',
                    color: '#1f2937',
                    margin: 0,
                    marginBottom: '8px'
                  }}>
                    {t('welcome')} {parentProfile?.full_name || parentProfile?.fullName || t('welcome')}
                  </h1>
                  <div style={{ color: '#6b7280', fontSize: windowWidth < 768 ? '12px' : '14px' }}>
                    {linkedStudents.length > 0 ? (
                      <span dir="auto">
                        {t('parentOf')} {linkedStudents.map(s => s.student_name).join(' • ')}
                      </span>
                    ) : (
                      <span>{t('noLinkedStudents')}</span>
                    )}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', width: windowWidth < 768 ? '100%' : 'auto' }}>
                <button
                  onClick={() => {
                    setLoading(true);
                    loadDashboardData();
                  }}
                  disabled={loading}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                      padding: windowWidth < 768 ? '8px 16px' : '10px 20px',
                    backgroundColor: '#4CAF50',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                      fontSize: windowWidth < 768 ? '14px' : '16px',
                    fontWeight: '500',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    opacity: loading ? 0.6 : 1,
                    transition: 'all 0.2s ease',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                      width: windowWidth < 768 ? '100%' : 'auto'
                  }}
                  onMouseOver={(e) => {
                    if (!loading) {
                      e.currentTarget.style.backgroundColor = '#45a049';
                      e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
                    }
                  }}
                  onMouseOut={(e) => {
                    if (!loading) {
                      e.currentTarget.style.backgroundColor = '#4CAF50';
                      e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                    }
                  }}
                >
                  <RefreshIcon 
                    size={18} 
                    style={{
                      animation: loading ? 'spin 1s linear infinite' : 'none'
                    }}
                  />
                  <span>{t('refresh')}</span>
                </button>
                </div>
              </div>
            </div>

            {/* Stats Grid */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: windowWidth < 768 ? 'repeat(1, 1fr)' : windowWidth < 1024 ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
              gap: windowWidth < 768 ? '12px' : '20px',
              marginBottom: windowWidth < 768 ? '24px' : '40px',
              width: '100%'
            }}>
              {/* متوسط الحفظ */}
              {/* <div style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                padding: windowWidth < 768 ? '16px' : '20px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                border: '1px solid #f0f0f0',
                textAlign: 'center',
                transition: 'all 0.2s ease',
                borderRight: '4px solid #4caf50'
              }}>
                <div style={{ 
                  fontSize: windowWidth < 768 ? '2rem' : '2.5rem', 
                  fontWeight: '300', 
                  color: '#4caf50',
                  marginBottom: '12px'
                }}>
                  {stats.avgHifz} {t('hizbUnit')}
                </div>
                <div style={{ 
                  color: '#424242', 
                  fontSize: windowWidth < 768 ? '14px' : '16px',
                  fontWeight: '400'
                }}>{t('averageHifz')}</div>
              </div> */}

              {/* متوسط الدرجات */}
              {/* <div style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                padding: windowWidth < 768 ? '16px' : '20px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                border: '1px solid #f0f0f0',
                textAlign: 'center',
                transition: 'all 0.2s ease',
                borderRight: '4px solid #2196f3'
              }}>
                <div style={{ 
                  fontSize: windowWidth < 768 ? '2rem' : '2.5rem', 
                  fontWeight: '300', 
                  color: '#2196f3',
                  marginBottom: '12px'
                }}>
                  {(stats.avgGrades || 0).toFixed(2)}/20
                </div>
                <div style={{ 
                  color: '#424242', 
                  fontSize: windowWidth < 768 ? '14px' : '16px',
                  fontWeight: '400'
                }}>{t('averageGrades')}</div>
              </div> */}

              {/* عدد المواد */}
              {/* <div style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                padding: windowWidth < 768 ? '16px' : '20px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                border: '1px solid #f0f0f0',
                textAlign: 'center',
                transition: 'all 0.2s ease',
                borderRight: '4px solid #ff9800'
              }}>
                <div style={{ 
                  fontSize: windowWidth < 768 ? '2rem' : '2.5rem', 
                  fontWeight: '300', 
                  color: '#ff9800',
                  marginBottom: '12px'
                }}>
                  {stats.subjectsCount}
                </div>
                <div style={{ 
                  color: '#424242', 
                  fontSize: windowWidth < 768 ? '14px' : '16px',
                  fontWeight: '400'
                }}>{t('subjectsCount')}</div>
              </div> */}

              {/* الرسائل المستلمة */}
              {/* <div style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                padding: windowWidth < 768 ? '16px' : '20px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                border: '1px solid #f0f0f0',
                textAlign: 'center',
                transition: 'all 0.2s ease',
                borderRight: '4px solid #9c27b0'
              }}>
                <div style={{ 
                  fontSize: windowWidth < 768 ? '2rem' : '2.5rem', 
                  fontWeight: '300', 
                  color: '#9c27b0',
                  marginBottom: '12px'
                }}>
                  {stats.messagesCount}
                </div>
                <div style={{ 
                  color: '#424242', 
                  fontSize: windowWidth < 768 ? '14px' : '16px',
                  fontWeight: '400'
                }}>{t('receivedMessages')}</div>
              </div> */}
            </div>

            {/* Recent Messages */}
            <div style={styles.card}>
              <h2 style={{
                fontSize: '1.25rem',
                fontWeight: '600',
                color: '#1f2937',
                marginBottom: '16px',
                textAlign: isRTL ? 'right' : 'left'
              }}>
                {t('recentMessages')}
              </h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {recentMessages.length > 0 ? (
                  recentMessages.map((message) => {
                    const priorityColors = {
                      normal: { bg: '#f3f4f6', text: '#374151', border: '#9ca3af' },
                      high: { bg: '#fef3c7', text: '#92400e', border: '#f59e0b' },
                      urgent: { bg: '#fee2e2', text: '#991b1b', border: '#ef4444' }
                    };
                    const colors = priorityColors[message.priority || 'normal'];
                    const reportType = translateReportType(message.subject);
                    const studentLabel = message.student_label ||
                      GLOBAL_UTILS.getStudentLabel(message.student_gender) ||
                      (message.student_id
                        ? GLOBAL_UTILS.getStudentLabel((linkedStudents.find(s => s.id === message.student_id || s.studentId === message.student_id) as any)?.gender)
                        : t('studentLabel'));

                    return (
                      <div
                        key={message.id}
                        style={{
                          backgroundColor: 'white',
                          borderRadius: '10px',
                          border: '1px solid #e5e7eb',
                          borderLeft: isRTL ? '1px solid #e5e7eb' : `4px solid ${colors.border}`,
                          borderRight: isRTL ? `4px solid ${colors.border}` : '1px solid #e5e7eb',
                          overflow: 'hidden',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.06)'
                        }}
                      >
                        {/* Ligne sujet + badge priorité */}
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '12px 16px',
                          backgroundColor: colors.bg,
                          borderBottom: '1px solid #e5e7eb',
                          gap: '8px',
                          flexDirection: isRTL ? 'row-reverse' : 'row'
                        }}>
                          <h3 style={{
                            fontSize: '1rem',
                            fontWeight: '700',
                            color: '#1f2937',
                            margin: 0,
                            flex: 1
                          }} dir="auto">
                            {reportType}
                          </h3>
                          <span style={{
                            padding: '3px 10px',
                            borderRadius: '12px',
                            fontSize: '11px',
                            fontWeight: '600',
                            backgroundColor: 'white',
                            color: colors.text,
                            border: `1px solid ${colors.border}`,
                            whiteSpace: 'nowrap',
                            flexShrink: 0
                          }}>
                            {translatePriority(message.priority || 'normal')}
                          </span>
                        </div>

                        {/* Métadonnées compactes */}
                        <div style={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: '6px 16px',
                          padding: '10px 16px',
                          borderBottom: '1px solid #f3f4f6',
                          alignItems: 'center',
                          flexDirection: isRTL ? 'row-reverse' : 'row'
                        }}>
                          {/* Expéditeur */}
                          <span style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px', color: '#6b7280', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
                            <span style={{ color: '#9ca3af' }}>👤</span>
                            <span style={{ color: '#374151' }}>{t('from')}</span>
                            <span style={{ color: '#1f2937', fontWeight: '600' }} dir="auto">{message.sender_name}</span>
                          </span>
                          {/* Étudiant concerné */}
                          {message.student_name && (
                            <span style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px', color: '#059669', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
                              <span>📖</span>
                              <span style={{ fontWeight: '500' }}>{studentLabel}:</span>
                              <span style={{ fontWeight: '600' }} dir="auto">{message.student_name}</span>
                            </span>
                          )}
                          {/* Date */}
                          <span style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', color: '#9ca3af', marginInlineStart: 'auto' }}>
                            <span>🕐</span>
                            <span style={{ direction: 'ltr' }}>
                              {GLOBAL_UTILS.formatDate(message.sent_date)}
                              {message.sent_time && ` ${message.sent_time}`}
                            </span>
                          </span>
                        </div>

                        {/* Contenu du message */}
                        <div style={{ padding: '12px 16px' }}>
                          <p style={{
                            color: '#374151',
                            fontSize: '14px',
                            lineHeight: '1.7',
                            margin: 0
                          }} dir="auto">
                            {message.content.length > 150
                              ? message.content.substring(0, 150) + '…'
                              : message.content}
                          </p>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div style={{ textAlign: 'center', color: '#6b7280', padding: '32px' }}>
                    <p style={{ fontSize: '1.1rem', margin: 0 }}>{t('noMessages')}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Grades Section */}
        {activeSection === 'myGrades' && (
          <div className="section-scrollable" style={{
            maxHeight: `calc(100vh - ${windowWidth < 768 ? '180px' : '200px'})`,
            overflowY: 'auto',
            overflowX: 'hidden',
            paddingRight: isRTL ? '0' : '8px',
            paddingLeft: isRTL ? '8px' : '0'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '32px',
              flexWrap: 'wrap',
              gap: '16px'
            }}>
            <h1 style={{
              fontSize: '1.875rem',
              fontWeight: 'bold',
              color: '#1f2937',
                margin: 0
            }}>
              {t('linkedStudentsGrades')}
            </h1>
              <button
                onClick={refreshGrades}
                disabled={refreshing === 'grades'}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '10px 16px',
                  backgroundColor: refreshing === 'grades' ? '#9ca3af' : '#9333ea',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: refreshing === 'grades' ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  transition: 'all 0.2s ease'
                }}
                onMouseOver={(e) => {
                  if (refreshing !== 'grades') {
                    e.currentTarget.style.backgroundColor = '#7e22ce';
                  }
                }}
                onMouseOut={(e) => {
                  if (refreshing !== 'grades') {
                    e.currentTarget.style.backgroundColor = '#9333ea';
                  }
                }}
              >
                <RefreshIcon animate={refreshing === 'grades'} />
                <span>{t('update')}</span>
              </button>
            </div>

            <div style={styles.card}>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ ...styles.table, direction: isRTL ? 'rtl' : 'ltr' }}>
                  <thead>
                    <tr>
                      <th style={{ ...styles.tableHeader, textAlign: isRTL ? 'right' : 'left' }}>{t('student')}</th>
                      <th style={{ ...styles.tableHeader, textAlign: isRTL ? 'right' : 'left' }}>{t('subject')}</th>
                      <th style={{ ...styles.tableHeader, textAlign: isRTL ? 'right' : 'left' }}>{t('grade')}</th>
                      <th style={{ ...styles.tableHeader, textAlign: isRTL ? 'right' : 'left' }}>{t('date')}</th>
                      <th style={{ ...styles.tableHeader, textAlign: isRTL ? 'right' : 'left' }}>{t('comment')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {linkedStudents.length === 0 ? (
                      <tr>
                        <td colSpan={5} style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                          {t('noLinkedStudents')}
                        </td>
                      </tr>
                    ) : grades.length === 0 ? (
                      <tr>
                        <td colSpan={5} style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                          {t('noGrades')}
                        </td>
                      </tr>
                    ) : (
                      grades.map((grade) => (
                        <tr key={grade.id} style={styles.tableRow}>
                          <td style={{ ...styles.tableCell, textAlign: isRTL ? 'right' : 'left' }}>
                            <span dir="auto">{grade.student_name}</span>
                          </td>
                          <td style={{ ...styles.tableCell, textAlign: isRTL ? 'right' : 'left' }}>
                            <span style={{
                              padding: '4px 12px',
                              borderRadius: '12px',
                              fontSize: '12px',
                              fontWeight: '600',
                              backgroundColor: '#e9d5ff',
                              color: '#7c3aed'
                            }}>
                              <span dir="auto">{grade.subject}</span>
                            </span>
                          </td>
                          <td style={styles.tableCell}>
                            <span style={{
                              fontSize: '1.125rem',
                              fontWeight: 'bold',
                              color: grade.grade >= 15 ? '#16a34a' : grade.grade >= 12 ? '#ca8a04' : '#dc2626'
                            }}>
                              {grade.grade}/20
                            </span>
                          </td>
                          <td style={styles.tableCell}>
                            {GLOBAL_UTILS.formatDate(grade.date)}
                          </td>
                          <td style={{ ...styles.tableCell, textAlign: isRTL ? 'right' : 'left' }}>
                            <span dir="auto">{grade.comment || '-'}</span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Quran Progress Section */}
        {activeSection === 'quranProgress' && (
          <QuranTrackingSection
            hideTitle={false}
            hideHeader={false}
            students={memoizedStudents}
            quranRecords={memoizedQuranRecords}
            onRefreshQuranRecords={refreshQuranRecords}
            onRefreshStats={handleRefreshStats}
            onGetStudents={handleGetStudents}
            onGetQuranRecordById={handleGetQuranRecordById}
            onAddQuranRecord={handleAddQuranRecord}
            onUpdateQuranRecord={handleUpdateQuranRecord}
            t={t}
            currentLanguage={currentLanguage}
            onGetNextSessionForStudent={handleGetNextSessionForStudent}
            onUpdateRepeatNewHifzInNextSession={handleUpdateRepeatNewHifzInNextSession}
            windowWidth={windowWidth}
            styles={styles}
            canModify={false}
            selectedStudentFilter={selectedStudentFilter}
            onStudentFilterChange={setSelectedStudentFilter}
            onRecordAdded={handleRecordAdded}
            onRecordUpdated={handleRecordUpdated}
          />
        )}

        {/* Old Quran Progress Section - REMOVED */}
        {false && activeSection === 'quranProgressOld' && (
          <div style={{ width: '100%', overflow: 'hidden', boxSizing: 'border-box', maxWidth: '100%' }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '32px',
              flexWrap: 'wrap',
              gap: '16px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                <h1 style={{ fontSize: '1.875rem', fontWeight: 'bold', color: '#1f2937', margin: 0 }}>
              {t('quranProgress')}
            </h1>
                {/* Filtre par enfant */}
                <select
                  value={selectedStudentFilter}
                  onChange={(e) => setSelectedStudentFilter(e.target.value)}
                  style={{
                    padding: '10px 16px',
                    borderRadius: '8px',
                    border: '1px solid #e5e7eb',
                    backgroundColor: 'white',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#374151',
                    cursor: 'pointer',
                    minWidth: '180px',
                    height: 'fit-content'
                  }}
                >
                  <option value="all">جميع الأطفال</option>
                  {linkedStudents.map(student => (
                    <option key={student.id} value={student.id.toString()}>
                      {student.student_name}
                    </option>
                  ))}
                </select>
              </div>
              <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
                <button
                  onClick={async () => {
                    setRefreshing('quran');
                    try {
                      // Utiliser refreshQuranRecords au lieu de dupliquer le code
                      await refreshQuranRecords();
                    } catch (error) {
                      console.error('Erreur lors du rafraîchissement:', error);
                    } finally {
                      setRefreshing(null);
                    }
                  }}
                  disabled={refreshing === 'quran'}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '10px 16px',
                    backgroundColor: refreshing === 'quran' ? '#9ca3af' : '#9333ea',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: refreshing === 'quran' ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseOver={(e) => {
                    if (refreshing !== 'quran') {
                      e.currentTarget.style.backgroundColor = '#7e22ce';
                    }
                  }}
                  onMouseOut={(e) => {
                    if (refreshing !== 'quran') {
                      e.currentTarget.style.backgroundColor = '#9333ea';
                    }
                  }}
                >
                  <RefreshIcon animate={refreshing === 'quran'} />
                  <span>{t('update')}</span>
                </button>
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              {(() => {
                // Filtrer les enfants selon la sélection
                const filteredStudents = selectedStudentFilter === 'all' 
                  ? linkedStudents 
                  : linkedStudents.filter(s => s.id.toString() === selectedStudentFilter);
                
                if (filteredStudents.length === 0) {
                  return (
                    <div key="no-students" style={{
                  ...styles.card,
                  textAlign: 'center',
                  padding: '48px',
                  color: '#6b7280'
                }}>
                      <p style={{ fontSize: '1.125rem' }}>
                        {linkedStudents.length === 0 ? t('noLinkedStudents') : 'لا يوجد أطفال مطابقين للفلتر'}
                      </p>
                </div>
                  ) as React.ReactElement;
                }
                
                return filteredStudents.map((student) => {
                  // Use student.studentId for filtering (it's the actual student ID from the database)
                  const studentRecords = quranRecords.filter(r => r.studentId === student.studentId || r.studentId === student.id);
                  console.log(`🔍 PARENT DASHBOARD - Étudiant "${student.student_name}" (ID: ${student.id}, studentId: ${student.studentId}) - Enregistrements trouvés:`, studentRecords.length);
                  
                  // Trier par date décroissante
                  const sortedRecords = studentRecords.sort((a, b) => {
                    const dateA = new Date(a.modifiedDate || 0).getTime();
                    const dateB = new Date(b.modifiedDate || 0).getTime();
                    if (dateB !== dateA) return dateB - dateA;
                    return 0;
                  });
                  
                  const latestRecord = sortedRecords[0];
                  
                  // Utiliser le totalHizb du dernier enregistrement
                  let totalHizb = 0;
                  if (latestRecord) {
                    const hizbValue = latestRecord.totalHizb;
                    if (typeof hizbValue === 'string') {
                      totalHizb = parseFloat(hizbValue) || 0;
                    } else if (typeof hizbValue === 'number') {
                      totalHizb = hizbValue;
                    }
                  }

                  let progressColor = '#ff9800';

                  if (totalHizb >= 20) {
                    progressColor = '#4caf50';
                  } else if (totalHizb >= 15) {
                    progressColor = '#2196f3';
                  } else if (totalHizb >= 5) {
                    progressColor = '#ffc107';
                  }

                  // Le Coran complet = 60 Hizb, calculer le pourcentage de progression
                  const progressPercentage = totalHizb > 0 ? Math.min((totalHizb / 60) * 100, 100) : 0;

                  return (
                    <div
                      key={student.id}
                      style={{
                        ...styles.card,
                        borderRight: `4px solid ${progressColor}`,
                        padding: '24px'
                      }}
                    >
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '16px',
                        gap: '16px'
                      }}>
                            <h3 style={{
                          fontSize: '1.125rem',
                              fontWeight: 'bold',
                              color: '#1f2937',
                          margin: 0,
                          flex: 1
                            }}>
                              {student.student_name}
                            </h3>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          flexShrink: 0
                        }}>
                          <div style={{
                            fontSize: '1.5rem',
                            fontWeight: 'bold',
                            color: '#4caf50',
                            lineHeight: '1.2'
                          }}>
                            {totalHizb > 0 ? totalHizb.toFixed(2) : '0'}
                          </div>
                          <div style={{ 
                            fontSize: '13px', 
                            color: '#6b7280',
                            lineHeight: '1.2'
                          }}>
                            {t('hizbMemorized')}
                          </div>
                        </div>
                      </div>

                      {/* Progress Bar */}
                      <div style={{ marginBottom: '16px' }}>
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          marginBottom: '6px'
                        }}>
                          <span style={{ fontSize: '13px', fontWeight: '500', color: '#374151' }}>
                            {t('progressLevel')}
                          </span>
                          <span style={{ fontSize: '13px', color: '#6b7280' }}>
                            {Math.round(progressPercentage)}% {t('percentageOfQuran')}
                          </span>
                        </div>
                        <div style={{
                          width: '100%',
                          height: '12px',
                          backgroundColor: '#e5e7eb',
                          borderRadius: '9999px',
                          overflow: 'hidden'
                        }}>
                          <div style={{
                            width: `${progressPercentage}%`,
                            height: '100%',
                            background: `linear-gradient(to right, ${progressColor}, ${progressColor}dd)`,
                            transition: 'width 0.5s ease'
                          }} />
                        </div>
                      </div>

                      {/* Latest Record */}
                      {latestRecord && (
                            <QuranRecordSection
                          latestRecord={latestRecord}
                              language={currentLanguage as 'ar' | 'fr' | 'en' || 'ar'}
                              cleanText={false}
                          onEdit={undefined}
                          canEdit={false}
                          windowWidth={windowWidth}
                          targetRepeatNewHifzCount={targetRepeatNewHifzCount}
                          onUpdateRepeatNewHifz={handleUpdateRepeatNewHifzInNextSession}
                            />
                      )}

                      {/* Previous Records Table */}
                      {sortedRecords.length > 1 && (
                        <div style={{
                          marginTop: '20px',
                          backgroundColor: '#f9fafb',
                          borderRadius: '8px',
                          padding: '16px',
                          overflow: 'hidden'
                        }}>
                          <h4 style={{
                            fontSize: '14px',
                            fontWeight: '600',
                            color: '#1f2937',
                            marginBottom: '12px',
                            paddingBottom: '8px',
                            borderBottom: '1px solid #e5e7eb'
                          }}>
                            الجلسات السابقة
                          </h4>
                          <div style={{ 
                            overflowX: 'auto',
                            overflowY: 'auto',
                            maxHeight: '400px',
                            border: '1px solid #e5e7eb',
                            borderRadius: '6px'
                          }}>
                            <table style={{
                              width: '100%',
                              borderCollapse: 'collapse',
                              fontSize: '13px',
                              minWidth: '600px'
                            }}>
                              <thead style={{ position: 'sticky', top: 0, zIndex: 10 }}>
                                <tr style={{
                                  backgroundColor: '#f3f4f6',
                                  borderBottom: '2px solid #e5e7eb'
                                }}>
                                  <th style={{
                                    padding: '10px 12px',
                                    textAlign: 'right',
                                    fontWeight: '600',
                                    color: '#374151',
                                    fontSize: '12px',
                                    backgroundColor: '#f3f4f6',
                                    whiteSpace: 'nowrap'
                                  }}>
                                    التاريخ
                                  </th>
                                  <th style={{
                                    padding: '10px 12px',
                                    textAlign: 'right',
                                    fontWeight: '600',
                                    color: '#374151',
                                    fontSize: '12px',
                                    backgroundColor: '#f3f4f6',
                                    whiteSpace: 'nowrap'
                                  }}>
                                    الحفظ الجديد
                                  </th>
                                  <th style={{
                                    padding: '10px 12px',
                                    textAlign: 'right',
                                    fontWeight: '600',
                                    color: '#374151',
                                    fontSize: '12px',
                                    backgroundColor: '#f3f4f6',
                                    whiteSpace: 'nowrap'
                                  }}>
                                    المراجعة
                                  </th>
                                  <th style={{
                                    padding: '10px 12px',
                                    textAlign: 'right',
                                    fontWeight: '600',
                                    color: '#374151',
                                    fontSize: '12px',
                                    backgroundColor: '#f3f4f6',
                                    whiteSpace: 'nowrap'
                                  }}>
                                    الواجب
                                  </th>
                                  <th style={{
                                    padding: '10px 12px',
                                    textAlign: 'right',
                                    fontWeight: '600',
                                    color: '#374151',
                                    fontSize: '12px',
                                    backgroundColor: '#f3f4f6',
                                    whiteSpace: 'nowrap'
                                  }}>
                                    الملاحظة
                                  </th>
                                  <th style={{
                                    padding: '10px 12px',
                                    textAlign: 'right',
                                    fontWeight: '600',
                                    color: '#374151',
                                    fontSize: '12px',
                                    backgroundColor: '#f3f4f6',
                                    whiteSpace: 'nowrap'
                                  }}>
                                    عدد الأحزاب
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {sortedRecords.slice(1).map((record, index) => {
                                  const hizbValue = record.totalHizb;
                                  let recordHizb = 0;
                                  if (typeof hizbValue === 'string') {
                                    recordHizb = parseFloat(hizbValue) || 0;
                                  } else if (typeof hizbValue === 'number') {
                                    recordHizb = hizbValue;
                                  }
                                  
                                  return (
                                    <tr
                                      key={record.id || index}
                                      style={{
                                        borderBottom: '1px solid #e5e7eb',
                                        backgroundColor: index % 2 === 0 ? 'white' : '#fafafa'
                                      }}
                                    >
                                      <td style={{
                                        padding: '10px 12px',
                                        color: '#6b7280',
                                        fontWeight: '500',
                                        whiteSpace: 'nowrap'
                                      }}>
                                        {GLOBAL_UTILS.formatDate(record.modifiedDate || '')}
                                      </td>
                                      <td style={{
                                        padding: '10px 12px',
                                        color: '#374151',
                                        maxWidth: '200px',
                                        wordBreak: 'break-word'
                                      }}>
                                        {GLOBAL_UTILS.cleanText(record.newHifzTxt || '-')}
                                      </td>
                                      <td style={{
                                        padding: '10px 12px',
                                        color: '#374151',
                                        maxWidth: '200px',
                                        wordBreak: 'break-word'
                                      }}>
                                        {GLOBAL_UTILS.cleanText(record.oldHifzMurajahTxt || '-')}
                                      </td>
                                      <td style={{
                                        padding: '10px 12px',
                                        color: '#374151',
                                        maxWidth: '200px',
                                        wordBreak: 'break-word'
                                      }}>
                                        {GLOBAL_UTILS.cleanText(record.nextHalaqaNewHifzText || '-')}
                                      </td>
                                      <td style={{
                                        padding: '10px 12px',
                                        color: '#374151',
                                        maxWidth: '200px',
                                        wordBreak: 'break-word'
                                      }}>
                                        {GLOBAL_UTILS.cleanText(record.note || '-')}
                                      </td>
                                      <td style={{
                                        padding: '10px 12px',
                                        color: '#374151',
                                        textAlign: 'center',
                                        fontWeight: '500',
                                        whiteSpace: 'nowrap'
                                      }}>
                                        {recordHizb > 0 ? recordHizb.toFixed(2) : '0'}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                        </div>
                        </div>
                      )}

                      {!latestRecord && (
                        <div style={{
                          backgroundColor: '#f9fafb',
                          borderRadius: '8px',
                          padding: '16px',
                          marginTop: '16px',
                          textAlign: 'center',
                          color: '#6b7280'
                        }}>
                          <p style={{ fontSize: '14px', margin: 0 }}>لا توجد سجلات متاحة</p>
                        </div>
                      )}
                    </div>
                  );
                });
              })()}
            </div>
          </div>
        )}

        {/* Messages Section */}
        {activeSection === 'myMessages' && (
          <div>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '32px',
              flexWrap: 'wrap',
              gap: '16px',
              flexDirection: isRTL ? 'row-reverse' : 'row'
            }}>
            <h1 style={{
              fontSize: '1.875rem',
              fontWeight: 'bold',
              color: '#1f2937',
                margin: 0
            }}>
                {t('myMessages')}
            </h1>
              <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
                <button
                  onClick={refreshMessages}
                  disabled={refreshing === 'messages'}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '10px 16px',
                    backgroundColor: refreshing === 'messages' ? '#9ca3af' : '#9333ea',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: refreshing === 'messages' ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseOver={(e) => {
                    if (refreshing !== 'messages') {
                      e.currentTarget.style.backgroundColor = '#7e22ce';
                    }
                  }}
                  onMouseOut={(e) => {
                    if (refreshing !== 'messages') {
                      e.currentTarget.style.backgroundColor = '#9333ea';
                    }
                  }}
                >
                  <RefreshIcon animate={refreshing === 'messages'} />
                  <span>{t('update')}</span>
                </button>
              <button 
                onClick={() => setShowSendMessageModal(true)}
                style={{
                  ...styles.button,
                  ...styles.primaryButton,
                  padding: '12px 24px',
                  fontSize: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                {t('sendMessage')}
              </button>
              </div>
            </div>

            {/* Onglets pour basculer entre messages reçus et envoyés */}
            <div style={{
              display: 'flex',
              gap: '8px',
              marginBottom: '24px',
              borderBottom: '2px solid #e5e7eb',
              flexDirection: isRTL ? 'row-reverse' : 'row'
            }}>
              <button
                onClick={() => setActiveMessageTab('received')}
                style={{
                  padding: '12px 24px',
                  fontSize: '16px',
                  fontWeight: '600',
                  border: 'none',
                  backgroundColor: activeMessageTab === 'received' ? '#f3e8ff' : 'transparent',
                  color: activeMessageTab === 'received' ? '#9333ea' : '#6b7280',
                  borderBottom: activeMessageTab === 'received' ? '3px solid #9333ea' : '3px solid transparent',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  marginBottom: '-2px',
                  borderRadius: activeMessageTab === 'received' ? '8px 8px 0 0' : '0'
                }}
              >
                {t('receivedMessages')}
              </button>
              <button
                onClick={() => setActiveMessageTab('sent')}
                style={{
                  padding: '12px 24px',
                  fontSize: '16px',
                  fontWeight: '600',
                  border: 'none',
                  backgroundColor: activeMessageTab === 'sent' ? '#f3e8ff' : 'transparent',
                  color: activeMessageTab === 'sent' ? '#9333ea' : '#6b7280',
                  borderBottom: activeMessageTab === 'sent' ? '3px solid #9333ea' : '3px solid transparent',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  marginBottom: '-2px',
                  borderRadius: activeMessageTab === 'sent' ? '8px 8px 0 0' : '0'
                }}
              >
                {t('sentMessages')}
              </button>
            </div>

            {/* Section Messages Reçus */}
            {activeMessageTab === 'received' && (
              <div style={styles.card}>
                <div className="messages-container" style={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  gap: '16px',
                  maxHeight: 'calc(100vh - 300px)',
                  overflowY: 'auto',
                  paddingRight: isRTL ? '0' : '8px',
                  paddingLeft: isRTL ? '8px' : '0'
                }}>
                  {messages.filter(msg => {
                    const senderType = msg.sender_type?.toLowerCase() || '';
                    return senderType !== 'parent' && senderType !== 'ولي الأمر';
                  }).length > 0 ? (
                    messages.filter(msg => {
                      const senderType = msg.sender_type?.toLowerCase() || '';
                      return senderType !== 'parent' && senderType !== 'ولي الأمر';
                    }).map((message) => {
                      const isOpen = expandedMessageId === message.id;
                      
                      // Fonction pour obtenir le genre de l'étudiant
                      const getStudentGender = (studentId: number | null | undefined): 'Male' | 'Female' | null | undefined => {
                        if (!studentId) return null;
                        const student = linkedStudents.find(s => s.id === studentId || s.studentId === studentId);
                        return student?.gender || null;
                      };
                      
                      return (
                        <MessageItem
                          key={message.id}
                          message={message as MessageItemData}
                          isOpen={isOpen}
                          onToggle={handleMessageClick}
                          useModernStyle={true}
                          windowWidth={windowWidth}
                          translateReportType={translateReportType}
                          translatePriority={translatePriority}
                          getPlainText={getPlainText}
                          getCleanedContent={getCleanedContent}
                          showSenderLabel={true}
                          senderLabel={t('from')}
                          getSenderTitle={getSenderTitle}
                          getStudentGender={getStudentGender}
                          getStudentLabel={GLOBAL_UTILS.getStudentLabel}
                          formatDate={GLOBAL_UTILS.formatDate}
                          showReplyButton={true}
                          onReply={(msg) => {
                            // Convertir MessageItemData en Message pour handleReplyToMessage
                            const messageForReply: Message = {
                              ...message,
                              sent_date: msg.sent_date || message.sent_date || ''
                            };
                            handleReplyToMessage(messageForReply);
                          }}
                          showReplyBadge={false}
                          t={t}
                        />
                      );
                    })
                  ) : (
                    <div style={{ textAlign: 'center', color: '#6b7280', padding: '32px' }}>
                      <p style={{ fontSize: '1.1rem', margin: 0 }}>{t('noReceivedMessages')}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Section Messages Envoyés */}
            {activeMessageTab === 'sent' && (
              <div style={styles.card}>
                <div className="messages-container" style={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  gap: '16px',
                  maxHeight: 'calc(100vh - 300px)',
                  overflowY: 'auto',
                  paddingRight: isRTL ? '0' : '8px',
                  paddingLeft: isRTL ? '8px' : '0'
                }}>
                  {messages.filter(msg => {
                    const senderType = msg.sender_type?.toLowerCase() || '';
                    return senderType === 'parent' || senderType === 'ولي الأمر';
                  }).length > 0 ? (
                    messages.filter(msg => {
                      const senderType = msg.sender_type?.toLowerCase() || '';
                      return senderType === 'parent' || senderType === 'ولي الأمر';
                    }).map((message) => {
                      const isOpen = expandedMessageId === message.id;
                      
                      // Fonction pour obtenir le genre de l'étudiant
                      const getStudentGender = (studentId: number | null | undefined): 'Male' | 'Female' | null | undefined => {
                        if (!studentId) return null;
                        const student = linkedStudents.find(s => s.id === studentId || s.studentId === studentId);
                        return student?.gender || null;
                      };
                      
                      return (
                        <MessageItem
                          key={message.id}
                          message={message as MessageItemData}
                          isOpen={isOpen}
                          onToggle={handleMessageClick}
                          useModernStyle={true}
                          windowWidth={windowWidth}
                          translateReportType={translateReportType}
                          translatePriority={translatePriority}
                          getPlainText={getPlainText}
                          getCleanedContent={getCleanedContent}
                          showSenderLabel={false}
                          senderLabel={t('to')}
                          getStudentGender={getStudentGender}
                          getStudentLabel={GLOBAL_UTILS.getStudentLabel}
                          formatDate={GLOBAL_UTILS.formatDate}
                          showReplyButton={false}
                          showReplyBadge={false}
                          t={t}
                        />
                      );
                    })
                  ) : (
                    <div style={{ textAlign: 'center', color: '#6b7280', padding: '32px' }}>
                      <p style={{ fontSize: '1.1rem', margin: 0 }}>{t('noSentMessages')}</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Send Message Modal */}
      {showSendMessageModal && (
        <div style={styles.modal}>
          <div style={styles.modalContent}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#1f2937', marginBottom: '16px' }}>
              {t('sendMessage')}
            </h2>
            <form onSubmit={handleSendParentMessage} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {/* Sélection de l'étudiant - Toujours visible, optionnel si "الإدارة" est cochée */}
              {linkedStudents.length > 1 && (
                <div style={styles.formGroup}>
                  <label style={styles.label}>
                    {t('selectStudent')}
                    {parentMessage.sendToTeachers && <span style={{ color: '#dc2626' }}> *</span>}
                    {!parentMessage.sendToTeachers && parentMessage.sendToAdmin && <span style={{ fontSize: '12px', color: '#666', fontWeight: 'normal' }}> ({t('optional')})</span>}
                    {!parentMessage.sendToAdmin && !parentMessage.sendToTeachers && <span style={{ fontSize: '12px', color: '#666', fontWeight: 'normal' }}> ({t('optional')})</span>}
                  </label>
                  <select 
                    value={parentMessage.studentId}
                    onChange={(e) => {
                      setParentMessage({
                        ...parentMessage, 
                        studentId: e.target.value
                      });
                    }}
                    required={parentMessage.sendToTeachers}
                    style={styles.select}
                  >
                    <option value="">{t('selectStudent')}</option>
                    {linkedStudents.map(student => (
                      <option key={student.id} value={student.id}>{student.student_name}</option>
                    ))}
                  </select>
                </div>
              )}
              
              {/* Checkboxes pour "الإدارة" et "الأساتذة" */}
              <div style={{ display: 'flex', flexDirection: 'row', gap: '12px', alignItems: 'stretch' }}>
                {/* Checkbox pour "الإدارة" */}
                <div style={{ ...styles.formGroup, flex: 1 }}>
                  <label 
                    style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '12px', 
                      padding: '12px',
                      cursor: 'pointer',
                      borderRadius: '6px',
                      backgroundColor: parentMessage.sendToAdmin ? '#ede9fe' : '#f9fafb',
                      border: parentMessage.sendToAdmin ? '2px solid #7c3aed' : '1px solid #d1d5db',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      if (!parentMessage.sendToAdmin) {
                        e.currentTarget.style.backgroundColor = '#f3f4f6';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!parentMessage.sendToAdmin) {
                        e.currentTarget.style.backgroundColor = '#f9fafb';
                      }
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={parentMessage.sendToAdmin}
                      onChange={(e) => {
                        setParentMessage({
                          ...parentMessage,
                          sendToAdmin: e.target.checked
                        });
                      }}
                      style={{ 
                        width: '20px', 
                        height: '20px', 
                        cursor: 'pointer',
                        accentColor: '#7c3aed'
                      }}
                    />
                    <span style={{ fontSize: '16px', color: '#374151', flex: 1, fontWeight: '600' }}>
                      {t('administration')}
                    </span>
                  </label>
                </div>

                {/* Checkbox pour "الأساتذة" */}
                <div style={{ ...styles.formGroup, flex: 1 }}>
                  <label 
                    style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '12px', 
                      padding: '12px',
                      cursor: 'pointer',
                      borderRadius: '6px',
                      backgroundColor: parentMessage.sendToTeachers ? '#e0f2fe' : '#f9fafb',
                      border: parentMessage.sendToTeachers ? '2px solid #0284c7' : '1px solid #d1d5db',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      if (!parentMessage.sendToTeachers) {
                        e.currentTarget.style.backgroundColor = '#f3f4f6';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!parentMessage.sendToTeachers) {
                        e.currentTarget.style.backgroundColor = '#f9fafb';
                      }
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={parentMessage.sendToTeachers}
                      onChange={(e) => {
                        setParentMessage({
                          ...parentMessage,
                          sendToTeachers: e.target.checked
                        });
                      }}
                      style={{ 
                        width: '20px', 
                        height: '20px', 
                        cursor: 'pointer',
                        accentColor: '#0284c7'
                      }}
                    />
                    <span style={{ fontSize: '16px', color: '#374151', flex: 1, fontWeight: '600' }}>
                      {t('teachers')}
                    </span>
                  </label>
                </div>

              </div>

              {/* Message d'information selon les sélections */}
              {(parentMessage.sendToAdmin || parentMessage.sendToTeachers) && (
                <div style={{ fontSize: '12px', color: '#666', marginTop: '0px', paddingLeft: isRTL ? '0' : '12px', paddingRight: isRTL ? '12px' : '0' }}>
                    {parentMessage.sendToAdmin && parentMessage.sendToTeachers && parentMessage.studentId && (
                      <span>{t('messageWillBeSentToAdminsAndTeachers')}</span>
                    )}
                    {parentMessage.sendToAdmin && parentMessage.sendToTeachers && !parentMessage.studentId && (
                      <span>{t('messageWillBeSentToAdminsSelectStudent')}</span>
                    )}
                    {parentMessage.sendToAdmin && !parentMessage.sendToTeachers && (
                      <span>{parentMessage.studentId ? t('messageWillBeSentToAdmins') : t('messageWillBeSentToAdminsOnly')}</span>
                    )}
                    {!parentMessage.sendToAdmin && parentMessage.sendToTeachers && parentMessage.studentId && (
                      <span>{t('messageWillBeSentToTeachersOnly')}</span>
                    )}
                    {!parentMessage.sendToAdmin && parentMessage.sendToTeachers && !parentMessage.studentId && (
                      <span>{t('selectStudentForTeachersMessage')}</span>
                    )}
                  </div>
                )}
              
              {/* Champ objet */}
              <div style={styles.formGroup}>
                <label style={styles.label}>{t('messageSubject')}</label>
                <input
                  type="text"
                  value={parentMessage.subject}
                  onChange={(e) => setParentMessage({...parentMessage, subject: e.target.value})}
                  style={styles.input}
                  placeholder={t('messageSubject')}
                />
              </div>
              
              {/* Champ message */}
              <div style={styles.formGroup}>
                <label style={styles.label}>{t('message')}</label>
                <textarea 
                  value={parentMessage.message}
                  onChange={(e) => setParentMessage({...parentMessage, message: e.target.value})}
                  required 
                  rows={4}
                  style={styles.textarea}
                  placeholder={t('messagePlaceholder')}
                />
              </div>
              
              <div style={styles.buttonGroup}>
                <button 
                  type="button"
                  onClick={closeSendMessageModal}
                  style={{
                    ...styles.button,
                    ...styles.secondaryButton,
                    ...styles.flexButton
                  }}
                >
                  {t('cancel')}
                </button>
                <button 
                  type="submit" 
                  style={{
                    ...styles.button,
                    ...styles.primaryButton,
                    ...styles.flexButton
                  }}
                >
                  {t('sendButton')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Reply Message Modal */}
      {showReplyMessageModal && messageToReplyTo && (
        <div style={styles.modal}>
          <div style={styles.modalContent}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#1f2937', marginBottom: '16px' }}>
              إجابة على الرسالة
            </h2>
            
            <form onSubmit={handleSendParentMessage} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {/* Affichage de l'étudiant - En lecture seule (pas de combobox) */}
              {messageToReplyTo.student_id && (
                <div style={styles.formGroup}>
                  <label style={styles.label}>
                    {t('selectStudent')}
                  </label>
                  <div style={{
                    padding: '12px',
                    backgroundColor: '#f3f4f6',
                    borderRadius: '6px',
                    border: '1px solid #d1d5db',
                    fontSize: '14px',
                    color: '#374151',
                    fontWeight: '500'
                  }}>
                    {messageToReplyTo.student_name || 
                     (linkedStudents.find(s => s.id === messageToReplyTo.student_id || s.studentId === messageToReplyTo.student_id)?.student_name) ||
                     `ID: ${messageToReplyTo.student_id}`
                    }
                  </div>
                </div>
              )}
              
              {/* Checkboxes pour "الإدارة" et "الأساتذة" */}
              <div style={{ display: 'flex', flexDirection: 'row', gap: '12px', alignItems: 'flex-start' }}>
                {/* Checkbox pour "الإدارة" */}
                <div style={{ ...styles.formGroup, flex: 1 }}>
                  <label style={styles.label}>
                    {t('administration')}
                  </label>
                  <label 
                    style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '12px', 
                      padding: '12px',
                      cursor: 'pointer',
                      borderRadius: '6px',
                      backgroundColor: parentMessage.sendToAdmin ? '#ede9fe' : '#f9fafb',
                      border: parentMessage.sendToAdmin ? '2px solid #7c3aed' : '1px solid #d1d5db',
                      transition: 'all 0.2s',
                      marginTop: '4px'
                    }}
                    onMouseEnter={(e) => {
                      if (!parentMessage.sendToAdmin) {
                        e.currentTarget.style.backgroundColor = '#f3f4f6';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!parentMessage.sendToAdmin) {
                        e.currentTarget.style.backgroundColor = '#f9fafb';
                      }
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={parentMessage.sendToAdmin}
                      onChange={(e) => {
                        setParentMessage({
                          ...parentMessage,
                          sendToAdmin: e.target.checked
                        });
                      }}
                      style={{ 
                        width: '20px', 
                        height: '20px', 
                        cursor: 'pointer',
                        accentColor: '#7c3aed'
                      }}
                    />
                    <span style={{ fontSize: '16px', color: '#374151', flex: 1, fontWeight: '600' }}>
                      {t('administration')}
                    </span>
                  </label>
                </div>

                {/* Affichage du nom du professeur (si c'est une réponse à un professeur) ou checkbox "الأساتذة" */}
                {messageToReplyTo && (messageToReplyTo.sender_type?.toLowerCase() === 'teacher' || messageToReplyTo.sender_type?.toLowerCase() === 'أستاذ') ? (
                  <div style={{ ...styles.formGroup, flex: 1 }}>
                    <label style={styles.label}>
                      {t('teachers')}
                    </label>
                    <div style={{
                      padding: '12px',
                      backgroundColor: '#e0f2fe',
                      borderRadius: '6px',
                      border: '2px solid #0284c7',
                      fontSize: '14px',
                      color: '#374151',
                      fontWeight: '500',
                      marginTop: '4px'
                    }}>
                      {messageToReplyTo.sender_name || t('teachers')}
                    </div>
                  </div>
                ) : (
                  <div style={{ ...styles.formGroup, flex: 1 }}>
                    <label style={styles.label}>
                      {t('teachers')}
                    </label>
                    <label 
                      style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '12px', 
                        padding: '12px',
                        cursor: 'pointer',
                        borderRadius: '6px',
                        backgroundColor: parentMessage.sendToTeachers ? '#e0f2fe' : '#f9fafb',
                        border: parentMessage.sendToTeachers ? '2px solid #0284c7' : '1px solid #d1d5db',
                        transition: 'all 0.2s',
                        marginTop: '4px'
                      }}
                      onMouseEnter={(e) => {
                        if (!parentMessage.sendToTeachers) {
                          e.currentTarget.style.backgroundColor = '#f3f4f6';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!parentMessage.sendToTeachers) {
                          e.currentTarget.style.backgroundColor = '#f9fafb';
                        }
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={parentMessage.sendToTeachers}
                        onChange={(e) => {
                          setParentMessage({
                            ...parentMessage,
                            sendToTeachers: e.target.checked
                          });
                        }}
                        style={{ 
                          width: '20px', 
                          height: '20px', 
                          cursor: 'pointer',
                          accentColor: '#0284c7'
                        }}
                      />
                      <span style={{ fontSize: '16px', color: '#374151', flex: 1, fontWeight: '600' }}>
                        {t('teachers')}
                      </span>
                    </label>
                  </div>
                )}

              </div>

              {/* Message d'information selon les sélections */}
              {(parentMessage.sendToAdmin || parentMessage.sendToTeachers) && (
                <div style={{ fontSize: '12px', color: '#666', marginTop: '0px', paddingLeft: isRTL ? '0' : '12px', paddingRight: isRTL ? '12px' : '0' }}>
                    {parentMessage.sendToAdmin && parentMessage.sendToTeachers && parentMessage.studentId && (
                      <span>{t('messageWillBeSentToAdminsAndTeachers')}</span>
                    )}
                    {parentMessage.sendToAdmin && parentMessage.sendToTeachers && !parentMessage.studentId && (
                      <span>{t('messageWillBeSentToAdminsSelectStudent')}</span>
                    )}
                    {parentMessage.sendToAdmin && !parentMessage.sendToTeachers && (
                      <span>{parentMessage.studentId ? t('messageWillBeSentToAdmins') : t('messageWillBeSentToAdminsOnly')}</span>
                    )}
                    {!parentMessage.sendToAdmin && parentMessage.sendToTeachers && parentMessage.studentId && (
                      <span>{t('messageWillBeSentToTeachersOnly')}</span>
                    )}
                    {!parentMessage.sendToAdmin && parentMessage.sendToTeachers && !parentMessage.studentId && (
                      <span>{t('selectStudentForTeachersMessage')}</span>
                    )}
                  </div>
                )}
              
              {/* Champ objet */}
              <div style={styles.formGroup}>
                <label style={styles.label}>{t('messageSubject')}</label>
                <input
                  type="text"
                  value={parentMessage.subject}
                  onChange={(e) => setParentMessage({...parentMessage, subject: e.target.value})}
                  style={styles.input}
                  placeholder={t('messageSubject')}
                />
              </div>
              
              {/* Champ message - Pré-rempli avec les informations du message original formatées (style Gmail) */}
              <div style={styles.formGroup}>
                <label style={styles.label}>{t('message')}</label>
                <textarea 
                  value={parentMessage.message}
                  onChange={(e) => setParentMessage({...parentMessage, message: e.target.value})}
                  required 
                  rows={15}
                  style={{
                    ...styles.textarea,
                    fontFamily: 'monospace',
                    fontSize: '13px',
                    lineHeight: '1.6',
                    whiteSpace: 'pre-wrap'
                  }}
                  placeholder={t('messagePlaceholder')}
                />
              </div>
              
              <div style={styles.buttonGroup}>
                <button 
                  type="button"
                  onClick={closeReplyMessageModal}
                  style={{
                    ...styles.button,
                    ...styles.secondaryButton,
                    ...styles.flexButton
                  }}
                >
                  {t('cancel')}
                </button>
                <button 
                  type="submit" 
                  style={{
                    ...styles.button,
                    ...styles.primaryButton,
                    ...styles.flexButton
                  }}
                >
                  {t('sendButton')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Change Password Modal */}
      <ChangePasswordModal
        isOpen={showChangePasswordModal}
        onClose={() => setShowChangePasswordModal(false)}
        onChangePassword={async (oldPassword, newPassword) => {
          const response = await apiService.changePassword({ oldPassword, newPassword });
          return response;
        }}
        t={t}
        styles={styles}
        dir={currentLanguage === 'ar' ? 'rtl' : 'ltr'}
      />
    </div>
  );
};

export default ParentDashboard;
